import React, { useEffect, useReducer, useRef, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FaCogs, FaEdit, FaSave, FaGoogle, FaSync, FaCheckCircle,
  FaSpinner, FaChevronRight, FaChevronLeft, FaTable, FaBolt,
  FaEye, FaCheck, FaTimes, FaLayerGroup, FaDatabase,
  FaSlidersH, FaExclamationTriangle, FaWifi, FaExternalLinkAlt,
  FaRedo, FaTrash, FaChartBar, FaUser, FaFileAlt, FaCalendarAlt,
  FaCloudUploadAlt, FaHourglassHalf,
  FaPlus,
  FaLightbulb, FaKey, FaHeading, FaAlignLeft, FaImage, FaMagic
} from "react-icons/fa";
import { SiGooglesheets } from "react-icons/si";
import { getApiBase } from "../apiBase";
import { useGoogleLogin } from "@react-oauth/google";

const getRecomApiBase = () => process.env.REACT_APP_API_BASE || getApiBase();

const extractColumnsFromTemplate = (template) => {
  if (!template) return [];
  const matches = [...template.matchAll(/\{([^}]+)\}/g)];
  return matches.map(m => m[1].trim()).filter(Boolean);
};

const buildColumnsFromConfig = (config) => {
  const cols = [];
  const seen = new Set();
  if (config.scoring_weights) {
    Object.keys(config.scoring_weights).forEach(key => {
      if (key !== 'semantic_relevance' && !seen.has(key)) {
        cols.push({ name: key, role: 'soft_preference', originalRole: 'soft_preference', manuallyModified: false, type: 'text' });
        seen.add(key);
      }
    });
  }
  const templateCols = extractColumnsFromTemplate(config.embed_template);
  templateCols.forEach(name => {
    if (!seen.has(name)) {
      let role = 'semantic';
      const lower = name.toLowerCase();
      if (lower.includes('price') || lower.includes('cost')) role = 'soft_preference';
      else if (lower.includes('category') || lower.includes('type')) role = 'facet';
      else if (lower.includes('rating') || lower.includes('score')) role = 'soft_preference';
      else if (lower.includes('id') && name.toLowerCase() !== 'course_id') role = 'identifier';
      cols.push({ name, role, originalRole: role, manuallyModified: false, type: 'text' });
      seen.add(name);
    }
  });
  return cols;
};

const FS = {
  IDLE: "IDLE",
  GOOGLE_CONNECTING: "GOOGLE_CONNECTING",
  GOOGLE_CANCELLED: "GOOGLE_CANCELLED",
  GOOGLE_FAILED: "GOOGLE_FAILED",
  PICKER_SDK_LOADING: "PICKER_SDK_LOADING",
  PICKER_SDK_FAILED: "PICKER_SDK_FAILED",
  PICKER_OPEN: "PICKER_OPEN",
  PICKER_CANCELLED: "PICKER_CANCELLED",
  AUTHENTICATED: "AUTHENTICATED",
  CHECKING_SCHEMA: "CHECKING_SCHEMA",
  SHEET_SELECTED: "SHEET_SELECTED",
  ANALYZING: "ANALYZING",
  ANALYZE_FAILED: "ANALYZE_FAILED",
  ANALYZE_TOKEN_EXPIRED: "ANALYZE_TOKEN_EXPIRED",
  ANALYZED: "ANALYZED",
  PREVIEWING: "PREVIEWING",
  PREVIEW_FAILED: "PREVIEW_FAILED",
  PREVIEWED: "PREVIEWED",
  EMBEDDING: "EMBEDDING",
  EMBED_FAILED: "EMBED_FAILED",
  EMBEDDED: "EMBEDDED",
};

const initialFlowState = {
  phase: FS.IDLE,
  googleToken: null,
  googleUser: null,
  sheetMeta: null,
  selectedSheet: "",
  selectedTab: "",
  gsStep: 1,
  columns: [],
  roleOptions: [],
  embedTemplate: "",
  scoringWeights: {},
  previewData: [],
  embedStage: -1,
  error: null,
  allColumnsIgnored: false,
  invalidTemplatePlaceholders: [],
  previewEmpty: false,
  existingSchema: false,
  existingConfig: null,
  embeddingProgress: 0,
  itemsEmbedded: 0,
  columnsMapped: 0,
  minSimilarity: 0,
  lastSynced: null,
  reembedMode: false,
  originalColumns: [],
  originalTemplate: "",
  originalWeights: {},
  analyzedRowCount: 0,
  storedRowCount: 0,
};

const SYNC_STAGES = [
  { label: "Connecting to Google Sheets...", pct: 15 },
  { label: "Fetching latest rows...", pct: 40 },
  { label: "Detecting schema changes...", pct: 65 },
  { label: "Indexing new entries...", pct: 85 },
  { label: "Finalizing sync...", pct: 95 },
];

const REEMBED_STAGES = [
  { label: "Loading catalog configuration...", pct: 10 },
  { label: "Rebuilding embeddings...", pct: 35 },
  { label: "Applying new template & weights...", pct: 60 },
  { label: "Persisting changes...", pct: 85 },
  { label: "Wrapping up...", pct: 95 },
];

const initialOpState = { loading: false, stageIdx: 0, progress: 0, error: null, done: false };

function flowReducer(state, action) {
  switch (action.type) {
    case "GOOGLE_START": return { ...state, phase: FS.GOOGLE_CONNECTING, error: null };
    case "GOOGLE_CANCEL": return { ...state, phase: FS.GOOGLE_CANCELLED, error: "Google sign-in was cancelled." };
    case "GOOGLE_POPUP_BLOCKED": return { ...state, phase: FS.GOOGLE_FAILED, error: "Popup blocked. Please allow popups for this site and try again." };
    case "GOOGLE_AUTH_FAILED": return { ...state, phase: FS.GOOGLE_FAILED, error: action.error || "Google authentication failed." };
    case "GOOGLE_AUTH_SUCCESS": return { ...state, phase: FS.AUTHENTICATED, googleToken: action.token, googleUser: action.user, error: null };
    case "CHECKING_SCHEMA": return { ...state, phase: FS.CHECKING_SCHEMA, error: null };
    case "PICKER_SDK_LOADING": return { ...state, phase: FS.PICKER_SDK_LOADING };
    case "PICKER_SDK_FAILED": return { ...state, phase: FS.PICKER_SDK_FAILED, error: "Google Picker failed to load. Check your connection or ad blockers." };
    case "PICKER_OPEN": return { ...state, phase: FS.PICKER_OPEN };
    case "PICKER_CANCEL": return { ...state, phase: FS.PICKER_CANCELLED, error: "No sheet was selected. Click 'Select a Google Sheet' to choose one." };
    case "SHEET_QUOTA_EXCEEDED": return { ...state, phase: FS.GOOGLE_FAILED, error: "Google API quota exceeded. Please try again later." };
    case "SHEET_NO_TABS": return { ...state, phase: FS.AUTHENTICATED, error: "This spreadsheet has no worksheets. Please choose a different sheet." };
    case "SHEET_SELECTED": return { ...state, phase: FS.SHEET_SELECTED, sheetMeta: action.sheetMeta, selectedSheet: action.sheetId, selectedTab: action.defaultTab, columns: [], roleOptions: [], embedTemplate: "", scoringWeights: {}, previewData: [], embedStage: -1, gsStep: 1, error: null, allColumnsIgnored: false, invalidTemplatePlaceholders: [], previewEmpty: false };
    case "TAB_CHANGE": return { ...state, selectedTab: action.tab };
    case "ANALYZE_START": return { ...state, phase: FS.ANALYZING, error: null, columns: [], roleOptions: [], embedTemplate: "", gsStep: 2, embeddingProgress: 0 };
    case "ANALYZE_PROGRESS": return { ...state, embeddingProgress: action.progress };
    case "ANALYZE_SUCCESS": return { ...state, phase: FS.ANALYZED, columns: action.columns, roleOptions: action.roleOptions, embedTemplate: action.embedTemplate, scoringWeights: Object.keys(state.scoringWeights || {}).length > 0 ? state.scoringWeights : action.scoringWeights, error: null, allColumnsIgnored: false, embeddingProgress: 100 };
    case "ANALYZE_FAILED": return { ...state, phase: FS.ANALYZE_FAILED, error: action.error, gsStep: 2, embeddingProgress: 0 };
    case "ANALYZE_TOKEN_EXPIRED": return { ...state, phase: FS.ANALYZE_TOKEN_EXPIRED, googleToken: null, googleUser: null, gsStep: 1, error: "Session expired. Please reconnect your Google account." };
    case "COLUMN_ROLE_CHANGE": return { ...state, columns: action.columns, scoringWeights: action.scoringWeights, allColumnsIgnored: action.allColumnsIgnored };
    case "WEIGHTS_CHANGE": return { ...state, scoringWeights: action.scoringWeights };
    case "EMBED_TEMPLATE_CHANGE": return { ...state, embedTemplate: action.template, invalidTemplatePlaceholders: action.invalidPlaceholders };
    case "PREVIEW_START": return { ...state, phase: FS.PREVIEWING, previewData: [], previewEmpty: false, error: null };
    case "PREVIEW_SUCCESS": return { ...state, phase: FS.PREVIEWED, previewData: action.samples, previewEmpty: action.samples.length === 0 };
    case "PREVIEW_FAILED": return { ...state, phase: FS.PREVIEW_FAILED, error: action.error };
    case "EMBED_START": return { ...state, phase: FS.EMBEDDING, embedStage: 0, error: null, embeddingProgress: 0 };
    case "EMBED_PROGRESS": return { ...state, embeddingProgress: action.progress };
    case "EMBED_STAGE": return { ...state, embedStage: action.stage };
    case "EMBED_SUCCESS": return { ...state, phase: FS.EMBEDDED, embedStage: -1, itemsEmbedded: action.itemsEmbedded || 0, columnsMapped: action.columnsMapped || 0, minSimilarity: action.minSimilarity || 0, lastSynced: new Date().toISOString() };
    case "EMBED_FAILED": return { ...state, phase: FS.EMBED_FAILED, embedding: false, embedStage: -1, error: action.error, embeddingProgress: 0 };
    case "STEP_CHANGE": return { ...state, gsStep: action.step };
    case "STEP_BACK": {
      const prev = state.gsStep - 1;
      if (prev < 1) return state;
      const patch = { gsStep: prev };
      if (state.gsStep === 3) { patch.previewData = []; patch.phase = FS.ANALYZED; patch.previewEmpty = false; }
      if (state.gsStep === 4 && state.phase !== FS.EMBEDDED) { patch.phase = FS.PREVIEWED; }
      return { ...state, ...patch };
    }
    case "DISCONNECT_GOOGLE": return { ...initialFlowState };
    case "SCHEMA_FOUND": {
      const rawColumnMapping = action.config.column_mapping || action.config.source_config?.column_mapping || {};
      const cols = Object.keys(rawColumnMapping).length > 0
        ? Object.entries(rawColumnMapping).map(([name, role]) => ({ name, role, originalRole: role, manuallyModified: false, type: "text" }))
        : buildColumnsFromConfig({ scoring_weights: action.config.scoring_weights || {}, embed_template: action.config.embed_template || "" });
      return { ...state, existingSchema: true, existingConfig: action.config, phase: FS.EMBEDDED, gsStep: 4, columns: cols, sheetMeta: { id: action.config.sheet_id || "existing", name: action.config.sheet_name || "Saved Catalog", tabs: [action.config.source_config?.worksheet || ""] }, selectedSheet: action.config.sheet_id || "existing", selectedTab: action.config.source_config?.worksheet || "", embedTemplate: action.config.embed_template || "", scoringWeights: action.config.scoring_weights || {}, itemsEmbedded: action.config.items_embedded || 0, columnsMapped: action.config.columns_mapped || 0, minSimilarity: action.config.min_similarity || 0, lastSynced: action.config.last_synced || null, googleUser: action.config.google_user || null };
    }
    case "SCHEMA_NOT_FOUND": return { ...state, existingSchema: false, existingConfig: null, phase: FS.AUTHENTICATED };
    case "RESET_FLOW": return { ...initialFlowState };
    case "UPDATE_DASHBOARD_STATS": return { ...state, itemsEmbedded: action.itemsEmbedded || state.itemsEmbedded, columnsMapped: action.columnsMapped || state.columnsMapped, minSimilarity: action.minSimilarity || state.minSimilarity, lastSynced: action.lastSynced || state.lastSynced };
    case "REEMBED_MODE_INIT": return { ...state, reembedMode: true, originalColumns: action.originalColumns, originalTemplate: action.originalTemplate, originalWeights: action.originalWeights, storedRowCount: action.storedRowCount, analyzedRowCount: 0 };
    case "REEMBED_ANALYZE_DONE": return { ...state, analyzedRowCount: action.rowCount };
    case "EXIT_REEMBED_MODE": return { ...state, reembedMode: false, originalColumns: [], originalTemplate: "", originalWeights: {}, analyzedRowCount: 0, storedRowCount: 0 };
    default: return state;
  }
}

const TYPE_COLORS = {
  number: { color: "#3B82F6", bg: "#EFF6FF" }, integer: { color: "#3B82F6", bg: "#EFF6FF" }, float: { color: "#3B82F6", bg: "#EFF6FF" },
  string: { color: "#7C3AED", bg: "#F5F3FF" }, text: { color: "#64748B", bg: "#F8FAFC" }, email: { color: "#059669", bg: "#ECFDF5" },
  date: { color: "#D97706", bg: "#FFFBEB" }, datetime: { color: "#EA580C", bg: "#FFF7ED" }, boolean: { color: "#DC2626", bg: "#FEF2F2" },
  category: { color: "#9333EA", bg: "#FAF5FF" }, url: { color: "#0EA5E9", bg: "#F0F9FF" },
};

const TypeBadge = ({ type }) => {
  const key = (type || "text").toLowerCase();
  const cfg = TYPE_COLORS[key] || TYPE_COLORS.text;
  return <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}25` }} className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide uppercase whitespace-nowrap">{type || "text"}</span>;
};

const Toggle = ({ value, onChange, disabled }) => (
  <div onClick={() => !disabled && onChange(!value)} className={`w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition ${value ? "bg-[#5B3A60]" : "bg-gray-300"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
    <div className={`bg-white w-5 h-5 rounded-full shadow transform transition ${value ? "translate-x-6" : "translate-x-0"}`} />
  </div>
);

const WeightSlider = ({ label, value, onChange, color = "#5B3A60" }) => {
  const percentage = Math.min(100, Math.max(0, (value / 0.5) * 100));
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-xs font-semibold text-[#4B334F] min-w-[120px] truncate">{label}</span>
      <div className="flex-1">
        <input type="range" min="0" max="0.5" step="0.005" value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-[#E2D7E2] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#5B3A60] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
          style={{ background: `linear-gradient(to right, ${color} ${percentage}%, #E2D7E2 ${percentage}%)` }} />
      </div>
      <span className="text-xs font-bold text-[#5B3A60] min-w-[45px] text-right">{value.toFixed(3)}</span>
    </div>
  );
};

const shimmerCSS = `
@keyframes shimmer { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }
@keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes spinIt { to { transform: rotate(360deg); } }
@keyframes pulseRing { 0% { box-shadow: 0 0 0 0 rgba(91,58,96,0.4); } 70% { box-shadow: 0 0 0 10px rgba(91,58,96,0); } 100% { box-shadow: 0 0 0 0 rgba(91,58,96,0); } }
@keyframes progFill { from { width: 0%; } to { width: 100%; } }
@keyframes modalEnter { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes backdropEnter { from { opacity: 0; } to { opacity: 1; } }
@keyframes dotBounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
@keyframes glowPulse { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
@keyframes slideInRight { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
@keyframes progressPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
.gs-shimmer { background: linear-gradient(to right, #f3f0f5 0%, #e8e0ec 25%, #f3f0f5 50%); background-size: 800px 100%; animation: shimmer 1.6s infinite linear; border-radius: 8px; }
.gs-fade-up { animation: fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) forwards; }
.gs-spin { animation: spinIt 0.9s linear infinite; }
.gs-pulse { animation: pulseRing 2s infinite; }
.gs-prog { animation: progFill 4.5s ease-in-out forwards; }
.gs-scroll::-webkit-scrollbar { width: 5px; }
.gs-scroll::-webkit-scrollbar-track { background: transparent; }
.gs-scroll::-webkit-scrollbar-thumb { background: #d1c7d8; border-radius: 4px; }
.modal-enter { animation: modalEnter 0.3s ease-out forwards; }
.modal-backdrop-enter { animation: backdropEnter 0.2s ease-out forwards; }
.dot-bounce { animation: dotBounce 1.4s ease-in-out infinite; }
.glow-pulse { animation: glowPulse 2s ease-in-out infinite; }
.slide-in-right { animation: slideInRight 0.3s ease-out forwards; }
.progress-pulse { animation: progressPulse 1.5s ease-in-out infinite; }
`;

const GS_STEPS = [
  { num: 1, label: "Connect", icon: FaGoogle },
  { num: 2, label: "Analyze", icon: FaTable },
  { num: 3, label: "Preview", icon: FaEye },
  { num: 4, label: "Finalize", icon: FaCheckCircle },
];

const EMBED_STAGES = [
  "Preparing your data...",
  "Processing content...",
  "Saving information...",
  "Wrapping things up...",
];

const ErrorBanner = ({ message, onRetry, retryLabel }) => (
  <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
    <FaExclamationTriangle className="text-red-500 flex-shrink-0 mt-0.5 text-sm" />
    <div className="flex-1 text-xs text-red-700 font-medium">{message}</div>
    {onRetry && (
      <button onClick={onRetry} className="flex items-center gap-1 text-[10px] font-bold text-red-600 hover:text-red-800 whitespace-nowrap transition-colors">
        <FaSync className="text-[9px]" /> {retryLabel || "Retry"}
      </button>
    )}
  </div>
);

const WarnBanner = ({ message }) => (
  <div className="flex items-start gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
    <FaExclamationTriangle className="text-orange-500 flex-shrink-0 mt-0.5 text-sm" />
    <div className="text-xs text-orange-700 font-medium">{message}</div>
  </div>
);

const LoadingStep = ({ icon: Icon, title, subtitle, progress }) => (
  <div className="flex flex-col items-center justify-center py-8 gs-fade-up">
    <div className="relative mb-5">
      <div className="w-16 h-16 rounded-2xl bg-[#5B3A60]/10 flex items-center justify-center glow-pulse">
        <Icon className="text-[#5B3A60] text-2xl" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-[#5B3A60]/20 flex items-center justify-center">
        <FaSpinner className="text-[#5B3A60] text-[8px] gs-spin" />
      </div>
    </div>
    <h3 className="text-sm font-bold text-gray-900 mb-1">{title}</h3>
    <p className="text-xs text-gray-500 mb-5 text-center max-w-xs leading-relaxed">{subtitle}</p>
    <div className="flex items-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="w-2 h-2 rounded-full bg-[#5B3A60] dot-bounce" style={{ animationDelay: `${i * 0.16}s` }} />
      ))}
    </div>
    {progress && (
      <div className="w-48 mt-4 bg-gray-100 rounded-full h-1 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full gs-prog" />
      </div>
    )}
  </div>
);

const WeightsModal = ({ open, onClose, columns, weights, onSave }) => {
  const [localWeights, setLocalWeights] = React.useState({});
  const [total, setTotal] = React.useState(0);
  React.useEffect(() => {
    if (!open) return;
    const dims = ["semantic_relevance"];
    columns.forEach((c) => { if (c.role === "hard_filter" || c.role === "soft_preference") dims.push(c.name); });
    const init = {};
    dims.forEach((d) => { init[d] = weights[d] ?? (d === "semantic_relevance" ? 0.35 : 0.1); });
    setLocalWeights(init);
  }, [open, columns, weights]);
  React.useEffect(() => { setTotal(Object.values(localWeights).reduce((a, b) => a + b, 0)); }, [localWeights]);
  const handleSave = () => { onSave(localWeights); onClose(); };
  const handleReset = () => {
    const dims = ["semantic_relevance"];
    columns.forEach((c) => { if (c.role === "hard_filter" || c.role === "soft_preference") dims.push(c.name); });
    const def = {};
    dims.forEach((d) => { def[d] = d === "semantic_relevance" ? 0.35 : 0.1; });
    setLocalWeights(def);
  };
  if (!open) return null;
  const dims = ["semantic_relevance"];
  columns.forEach((c) => { if (c.role === "hard_filter" || c.role === "soft_preference") dims.push(c.name); });
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm modal-backdrop-enter" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-[#E5DCE7] overflow-hidden modal-enter">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0EAF2] bg-gradient-to-r from-[#5B3A60]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center"><FaSlidersH className="text-[#5B3A60] text-sm" /></div>
            <div><h3 className="text-sm font-extrabold text-gray-900">Scoring Weights</h3><p className="text-[10px] text-gray-500">Configure ranking priorities</p></div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"><FaTimes className="text-xs" /></button>
        </div>
        <div className="px-6 py-4 max-h-[50vh] overflow-y-auto gs-scroll">
          <div className="mb-4 p-3 bg-[#5B3A60]/5 border border-[#5B3A60]/20 rounded-xl space-y-1">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="font-bold text-[#5B3A60]">Current total:</span>
              <span className={`font-bold ${Math.abs(total - 1) < 0.001 ? "text-green-600" : "text-orange-500"}`}>{total.toFixed(3)}</span>
              {Math.abs(total - 1) >= 0.001 && <span className="text-gray-400">(will normalize on save)</span>}
            </div>
            <div className="mt-2 border-t border-[#5B3A60]/10 pt-2" />
          </div>
          <div className="space-y-1">
            {dims.map((dim) => (
              <div key={dim} className={`p-3 rounded-xl border transition-all ${dim === "semantic_relevance" ? "bg-[#5B3A60]/5 border-[#5B3A60]/30" : "bg-gray-50 border-gray-100"}`}>
                <WeightSlider label={dim === "semantic_relevance" ? "🎯 Semantic Relevance" : `📊 ${dim}`} value={localWeights[dim] ?? 0.1} onChange={(val) => setLocalWeights((prev) => ({ ...prev, [dim]: val }))} color={dim === "semantic_relevance" ? "#5B3A60" : "#7B5A80"} />
                {dim !== "semantic_relevance" && (<p className="text-[9px] text-gray-400 ml-[120px] mt-1">Role: {columns.find((c) => c.name === dim)?.role}</p>)}
              </div>
            ))}
          </div>
          {dims.length === 1 && (<div className="text-center py-6 text-xs text-gray-400"><FaSlidersH className="mx-auto mb-2 text-gray-300" />No filter/preference columns detected.</div>)}
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#F0EAF2] bg-gray-50/50">
          <button onClick={handleReset} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 transition-all"><FaSync className="text-[10px]" /> Reset Defaults</button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
            <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-xs font-bold hover:shadow-lg hover:scale-105 transition-all">Save Weights</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigViewModal = ({ open, onClose, flow }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm modal-backdrop-enter" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-[#E5DCE7] overflow-hidden modal-enter">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0EAF2] bg-gradient-to-r from-[#5B3A60]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center"><FaCogs className="text-[#5B3A60] text-sm" /></div>
            <div><h3 className="text-sm font-extrabold text-gray-900">Catalog Configuration</h3><p className="text-[10px] text-gray-500">Read-only view</p></div>
          </div>
        </div>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto gs-scroll space-y-6">
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2"><FaTable className="text-[#5B3A60]" /> Column Mappings</h4>
            <div className="border border-[#E2D7E2] bg-[#FBF8FA] rounded-xl overflow-hidden">
              {flow.columns && flow.columns.length > 0 ? (
                <div className="max-h-[420px] overflow-y-auto gs-scroll">
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#F0EAF2] border-b border-[#E2D7E2] sticky top-0 z-10">
                    <div className="col-span-7 text-[10px] font-bold text-gray-500 uppercase">Column Name</div>
                    <div className="col-span-5 text-[10px] font-bold text-gray-500 uppercase">Role</div>
                  </div>
                  {flow.columns.map((col) => {
                    const ROLE_LABELS = { identifier: "Identifier", title: "Title", description: "Description", semantic: "Semantic", hard_filter: "Hard Filter", soft_preference: "Soft Preference", facet: "Facet", media: "Media", metadata: "Metadata", ignore: "Ignored" };
                    const ROLE_STYLES = { identifier: "bg-blue-100 text-blue-700 border-blue-200", title: "bg-purple-100 text-purple-700 border-purple-200", description: "bg-green-100 text-green-700 border-green-200", semantic: "bg-indigo-100 text-indigo-700 border-indigo-200", hard_filter: "bg-red-100 text-red-700 border-red-200", soft_preference: "bg-orange-100 text-orange-700 border-orange-200", facet: "bg-pink-100 text-pink-700 border-pink-200", media: "bg-cyan-100 text-cyan-700 border-cyan-200", metadata: "bg-gray-100 text-gray-700 border-gray-200", ignore: "bg-gray-100 text-gray-400 border-gray-200" };
                    return (
                      <div key={col.name} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center border-b border-[#F0EAF2] last:border-0 hover:bg-[#5B3A60]/2 transition-colors">
                        <div className="col-span-7"><p className="text-xs font-mono font-semibold text-gray-800 truncate" title={col.name}>{col.name}</p></div>
                        <div className="col-span-5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${ROLE_STYLES[col.role] || ROLE_STYLES.metadata}`}>{ROLE_LABELS[col.role] || col.role}</span></div>
                      </div>
                    );
                  })}
                </div>
              ) : (<div className="px-4 py-6 text-center text-xs text-gray-400">No columns configured</div>)}
            </div>
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2"><FaFileAlt className="text-[#5B3A60]" /> Embed Template</h4>
            <div className="border border-[#E2D7E2] bg-[#FBF8FA] rounded-xl px-4 py-3"><p className="text-sm font-mono text-[#5B3A60] break-all">{flow.embedTemplate || "—"}</p></div>
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2"><FaSlidersH className="text-[#5B3A60]" /> Scoring Weights</h4>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(flow.scoringWeights).length > 0 ? (
                Object.entries(flow.scoringWeights).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 capitalize">{key.replace(/_/g, " ")}</span>
                      <span className="text-xs font-bold text-[#5B3A60] font-mono bg-[#5B3A60]/10 px-2 py-0.5 rounded">{value?.toFixed(3)}</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full" style={{ width: `${Math.min(value * 100, 100)}%` }} /></div>
                  </div>
                ))
              ) : (<div className="col-span-2 text-center py-4 text-xs text-gray-400">No scoring weights configured</div>)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end px-6 py-4 border-t border-[#F0EAF2] bg-gray-50/50">
          <button onClick={onClose} className="px-5 py-2 rounded-xl bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B] transition-all">Close</button>
        </div>
      </div>
    </div>
  );
};

const Terminal = ({ logs, onProceed, isProceeding }) => (
  <div className="bg-gray-900 text-gray-100 font-mono text-xs p-4 rounded-xl overflow-y-auto max-h-64 gs-scroll border border-gray-700 shadow-inner mt-4">
    {logs.map((log, i) => (
      <div key={i} className={`mb-1.5 flex items-start gap-2 ${log.type === 'error' ? 'text-red-400' : log.type === 'warning' ? 'text-yellow-400' : log.type === 'action' ? 'text-blue-400' : 'text-green-400'}`}>
        <span className="text-gray-500 shrink-0 select-none">[{new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]</span>
        <span className="break-all">{log.type === 'error' ? '✖' : log.type === 'warning' ? '⚠' : log.type === 'action' ? '➜' : '✓'} {log.message}</span>
      </div>
    ))}
    {isProceeding && (
      <div className="mt-4 pt-4 border-t border-gray-700">
        <div className="flex items-center gap-3 mb-2">
          <FaSpinner className="gs-spin text-blue-400" />
          <span className="text-blue-300 text-xs font-semibold">Analyzing your data, please wait a few minutes...</span>
        </div>
        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full gs-prog" style={{ animation: 'progFill 8s ease-in-out forwards' }} /></div>
        <p className="text-[10px] text-gray-500 mt-2">Detecting schema changes and preparing for re-embedding</p>
      </div>
    )}
    {onProceed && !isProceeding && (
      <div className="mt-4 pt-4 border-t border-gray-700 flex justify-end">
        <button onClick={onProceed} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700">Proceed with Re-embed →</button>
      </div>
    )}
    {!onProceed && !isProceeding && <div className="animate-pulse text-green-400 mt-1">_</div>}
  </div>
);

const ReembedReviewModal = ({ open, onClose, data, flow, onUpdateTemplate, onOpenWeights, onConfirm, isConfirming, dispatch }) => {
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedNewCol, setSelectedNewCol] = useState(null);
  const [selectedRole, setSelectedRole] = useState("semantic");
  const [showExistingColumns, setShowExistingColumns] = useState(false);
  if (!open || !data) return null;
  const invalidPlaceholders = validateTemplatePlaceholders(flow.embedTemplate, flow.columns);
  const handleOpenRoleModal = (col) => {
    setSelectedNewCol(col);
    const existingCol = flow.columns.find(c => c.name === col.name);
    setSelectedRole(existingCol?.role || col.suggested_role || "semantic");
    setRoleModalOpen(true);
  };
  const handleSaveRole = () => {
    if (!selectedNewCol || !dispatch) return;
    const currentColumns = flow.columns.map(c => ({ ...c }));
    const newColIndex = currentColumns.findIndex(c => c.name === selectedNewCol.name);
    let updatedColumns;
    let newWeights = { ...flow.scoringWeights };
    if (newColIndex >= 0) {
      const previousRole = currentColumns[newColIndex].role;
      currentColumns[newColIndex].role = selectedRole;
      currentColumns[newColIndex].manuallyModified = true;
      currentColumns[newColIndex].originalRole = selectedRole;
      const wasScoring = previousRole === "hard_filter" || previousRole === "soft_preference";
      const isScoring = selectedRole === "hard_filter" || selectedRole === "soft_preference";
      if (wasScoring && !isScoring) delete newWeights[selectedNewCol.name];
      else if (!wasScoring && isScoring) newWeights[selectedNewCol.name] = selectedRole === "hard_filter" ? 0.2 : 0.1;
      updatedColumns = currentColumns;
    } else {
      const isScoringRole = selectedRole === "hard_filter" || selectedRole === "soft_preference";
      if (isScoringRole && !newWeights[selectedNewCol.name]) newWeights[selectedNewCol.name] = selectedRole === "hard_filter" ? 0.2 : 0.1;
      currentColumns.push({ name: selectedNewCol.name, role: selectedRole, originalRole: selectedRole, manuallyModified: true, type: "text", isNew: true });
      updatedColumns = currentColumns;
    }
    dispatch({ type: "COLUMN_ROLE_CHANGE", columns: updatedColumns, scoringWeights: newWeights, allColumnsIgnored: false });
    dispatch({ type: "WEIGHTS_CHANGE", scoringWeights: newWeights });
    setRoleModalOpen(false);
    setSelectedNewCol(null);
  };
  const handleEditExistingRole = (colName, newRole) => {
    const currentColumns = flow.columns.map(c => ({ ...c }));
    const colIndex = currentColumns.findIndex(c => c.name === colName);
    if (colIndex === -1) return;
    const previousRole = currentColumns[colIndex].role;
    currentColumns[colIndex].role = newRole;
    currentColumns[colIndex].manuallyModified = true;
    let newWeights = { ...flow.scoringWeights };
    const wasScoring = previousRole === "hard_filter" || previousRole === "soft_preference";
    const isScoring = newRole === "hard_filter" || newRole === "soft_preference";
    if (wasScoring && !isScoring) delete newWeights[colName];
    else if (!wasScoring && isScoring) newWeights[colName] = newRole === "hard_filter" ? 0.2 : 0.1;
    dispatch({ type: "COLUMN_ROLE_CHANGE", columns: currentColumns, scoringWeights: newWeights, allColumnsIgnored: false });
    dispatch({ type: "WEIGHTS_CHANGE", scoringWeights: newWeights });
  };
  return (
    <>
      <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/55 backdrop-blur-sm modal-backdrop-enter" onClick={onClose} />
        <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-[#E5DCE7] overflow-hidden modal-enter flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0EAF2] bg-gradient-to-r from-orange-50 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center"><FaRedo className="text-orange-600 text-sm" /></div>
              <div><h3 className="text-sm font-extrabold text-gray-900">Review Schema Changes</h3><p className="text-[10px] text-gray-500">Confirm updates before re-embedding</p></div>
            </div>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"><FaTimes className="text-xs" /></button>
          </div>
          <div className="px-6 py-5 overflow-y-auto gs-scroll space-y-5">
            <div>
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2"><FaTable className="text-[#5B3A60]" /> Column Changes</h4>
              <div className="space-y-3">
                {data.new_columns && data.new_columns.length > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-green-800 mb-2 flex items-center gap-2"><FaBolt className="text-[10px]" /> New Columns Detected</p>
                    <div className="space-y-2">
                      {data.new_columns.map(col => {
                        const currentCol = flow.columns.find(c => c.name === col.name);
                        const currentRole = currentCol ? currentCol.role : col.suggested_role;
                        return (
                          <div key={col.name} className="flex items-center justify-between bg-white p-2 rounded-lg border border-green-100">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">{col.name}</span>
                              <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">NEW</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${currentRole === 'ignore' ? 'bg-gray-100 text-gray-500' : currentRole === 'semantic' ? 'bg-indigo-100 text-indigo-700' : currentRole === 'hard_filter' ? 'bg-red-100 text-red-700' : currentRole === 'soft_preference' ? 'bg-orange-100 text-orange-700' : currentRole === 'facet' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'}`}>{currentRole.replace('_', ' ')}</span>
                            </div>
                            <button onClick={() => handleOpenRoleModal(col)} className="w-6 h-6 flex items-center justify-center rounded-full bg-[#5B3A60] text-white hover:bg-[#462B4B] transition-colors" title="Assign Role"><FaCheck className="text-[8px]" /></button>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-green-600 mt-2">Tip: Click the checkmark to assign a role. Filters/Preferences will auto-add to weights.</p>
                  </div>
                ) : (<div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-500">No new columns detected.</div>)}
                {data.removed_columns && data.removed_columns.length > 0 ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-red-800 mb-2 flex items-center gap-2"><FaTrash className="text-[10px]" /> Removed Columns</p>
                    <div className="flex flex-wrap gap-2">
                      {data.removed_columns.map(colName => (<span key={colName} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-red-200 text-red-700 text-xs font-semibold line-through opacity-70"><FaTrash className="text-[8px]" /> {colName}</span>))}
                    </div>
                    <p className="text-[10px] text-red-600 mt-2">These columns will be removed from the schema and embeddings.</p>
                  </div>
                ) : (<div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-500">No removed columns detected.</div>)}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2"><FaLayerGroup className="text-[#5B3A60]" /> Existing Columns & Roles</label>
                <button onClick={() => setShowExistingColumns(!showExistingColumns)} className="text-[10px] text-[#5B3A60] font-bold hover:underline flex items-center gap-1">{showExistingColumns ? "Hide" : "Edit Roles"} <FaEdit className="text-[8px]" /></button>
              </div>
              {showExistingColumns && (
                <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 max-h-60 overflow-y-auto gs-scroll">
                  <div className="grid grid-cols-12 gap-2 px-2 py-1 bg-gray-50 rounded-lg mb-2 sticky top-0">
                    <div className="col-span-6 text-[9px] font-bold text-gray-500 uppercase">Column Name</div>
                    <div className="col-span-6 text-[9px] font-bold text-gray-500 uppercase">Current Role</div>
                  </div>
                  {flow.columns.filter(c => c.role !== 'ignore').map((col) => (
                    <div key={col.name} className="grid grid-cols-12 gap-2 px-2 py-2 items-center border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <div className="col-span-6"><span className="text-xs font-mono font-semibold text-gray-700 truncate block" title={col.name}>{col.name}</span></div>
                      <div className="col-span-6">
                        <select value={col.role} onChange={(e) => handleEditExistingRole(col.name, e.target.value)} className="w-full text-[10px] border border-gray-300 rounded px-1 py-1 bg-white focus:ring-1 focus:ring-[#5B3A60] outline-none">
                          {["identifier", "title", "description", "semantic", "hard_filter", "soft_preference", "facet", "media", "metadata", "ignore"].map(r => (<option key={r} value={r}>{r.replace('_', ' ')}</option>))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2"><FaFileAlt className="text-[#5B3A60]" /> Embed Template</h4>
                <span className="text-[10px] text-gray-500">Update to include new columns</span>
              </div>
              <textarea rows={3} value={flow.embedTemplate} onChange={(e) => onUpdateTemplate(e.target.value)} className="w-full border border-[#D4C7D8] rounded-xl px-4 py-3 text-sm font-mono text-gray-700 focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none bg-white" placeholder="e.g. {title} {description}" />
              {invalidPlaceholders?.length > 0 && (<div className="mt-2"><WarnBanner message={`Unknown placeholders: ${invalidPlaceholders.join(", ")}.`} /></div>)}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#4B334F]">Scoring Weights</h3>
                <button onClick={onOpenWeights} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B] transition-all"><FaSlidersH className="text-[10px]" /> Configure</button>
              </div>
              <div className="border border-[#E8DDE8] rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#FAF7FB] border-b border-[#EFE5F1]">
                  <div className="col-span-7 text-[10px] font-bold uppercase text-gray-500">Dimension</div>
                  <div className="col-span-5 text-[10px] font-bold uppercase text-gray-500">Weight</div>
                </div>
                <div className="divide-y divide-[#F4EDF5] max-h-48 overflow-y-auto gs-scroll">
                  {Object.entries(flow.scoringWeights).map(([key, value]) => (
                    <div key={key} className="grid grid-cols-12 gap-2 px-4 py-3 items-center bg-white">
                      <div className="col-span-7"><p className="text-xs font-semibold text-gray-800 break-all">{key}</p></div>
                      <div className="col-span-5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-[#EFE5F1] overflow-hidden"><div className="h-full bg-[#5B3A60] rounded-full" style={{ width: `${Math.min(value * 100, 100)}%` }} /></div>
                          <span className="text-[11px] font-bold text-[#5B3A60] min-w-[42px] text-right">{value.toFixed(3)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#F0EAF2] bg-gray-50/50">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-all">Cancel</button>
            <button onClick={onConfirm} disabled={isConfirming || invalidPlaceholders?.length > 0} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              {isConfirming ? <><FaSpinner className="gs-spin text-[10px]" /> Updating...</> : <><FaCheck className="text-[10px]" /> Confirm & Re-embed</>}
            </button>
          </div>
        </div>
      </div>
      {roleModalOpen && selectedNewCol && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setRoleModalOpen(false)} />
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-xl border border-[#E5DCE7] p-6 modal-enter">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Assign Role for <span className="text-[#5B3A60]">{selectedNewCol.name}</span></h3>
            <div className="space-y-3 mb-6">
              {[{ val: "semantic", label: "Semantic" }, { val: "hard_filter", label: "Hard Filter" }, { val: "soft_preference", label: "Soft Preference" }, { val: "facet", label: "Facet" }, { val: "identifier", label: "Identifier" }, { val: "metadata", label: "Metadata" }, { val: "description", label: "Description" }, { val: "ignore", label: "Ignore" }].map(opt => (
                <div key={opt.val} onClick={() => setSelectedRole(opt.val)} className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedRole === opt.val ? 'border-[#5B3A60] bg-[#5B3A60]/5 ring-1 ring-[#5B3A60]' : 'border-gray-200 hover:border-gray-300'}`}>
                  <p className="text-xs font-bold text-gray-800">{opt.label}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRoleModalOpen(false)} className="px-4 py-2 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleSaveRole} className="px-4 py-2 rounded-lg bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B]">Save Role</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function useToast() {
  const [toast, setToast] = React.useState(null);
  const timerRef = useRef(null);
  const show = useCallback((type, msg) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ type, msg });
    timerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  return [toast, show];
}

function validateTemplatePlaceholders(template, columns) {
  const validNames = new Set(columns.filter((c) => c.role !== "ignore").map((c) => c.name));
  const found = [...template.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
  return found.filter((p) => !validNames.has(p));
}

const ChangeSummary = ({ changes, hasNewRows, storedRowCount, analyzedRowCount }) => {
  if (!changes.hasColumnChanges && !hasNewRows) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl">
        <FaCheckCircle className="text-green-500 text-sm" />
        <span className="text-xs font-medium text-green-700">No changes detected — catalog is already up to date.</span>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {hasNewRows && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
          <FaDatabase className="text-blue-500 text-xs" />
          <span className="text-xs font-medium text-blue-700">{analyzedRowCount - storedRowCount} new row(s) detected — will sync after re-embed.</span>
        </div>
      )}
      {changes.newColumns.length > 0 && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-purple-50 border border-purple-100 rounded-xl">
          <FaBolt className="text-purple-500 text-xs mt-0.5" />
          <div><span className="text-xs font-bold text-purple-700">New columns: </span><span className="text-xs text-purple-600">{changes.newColumns.map(c => c.name).join(", ")}</span></div>
        </div>
      )}
      {changes.roleChanged.length > 0 && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-orange-50 border border-orange-100 rounded-xl">
          <FaSlidersH className="text-orange-500 text-xs mt-0.5" />
          <div><span className="text-xs font-bold text-orange-700">Role changes: </span><span className="text-xs text-orange-600">{changes.roleChanged.map(c => `${c.name}`).join(", ")}</span></div>
        </div>
      )}
      {changes.removedColumns.length > 0 && (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl">
          <FaTimes className="text-red-500 text-xs mt-0.5" />
          <div><span className="text-xs font-bold text-red-700">Columns set to ignore: </span><span className="text-xs text-red-600">{changes.removedColumns.map(c => c.name).join(", ")}</span></div>
        </div>
      )}
      {changes.templateChanged && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-50 border border-yellow-100 rounded-xl">
          <FaFileAlt className="text-yellow-600 text-xs" />
          <span className="text-xs font-medium text-yellow-700">Embed template modified.</span>
        </div>
      )}
      {changes.weightsChanged && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-50 border border-yellow-100 rounded-xl">
          <FaSlidersH className="text-yellow-600 text-xs" />
          <span className="text-xs font-medium text-yellow-700">Scoring weights modified.</span>
        </div>
      )}
    </div>
  );
};

const DeleteConfirmModal = ({ open, onClose, onConfirm, flow }) => {
  const [confirmText, setConfirmText] = useState("");
  const [step, setStep] = useState(1);
  useEffect(() => { if (open) { setStep(1); setConfirmText(""); } }, [open]);
  if (!open) return null;
  const handleNext = () => setStep(2);
  const handleFinalConfirm = () => { if (confirmText.toLowerCase() === "delete") onConfirm(); };
  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm modal-backdrop-enter" onClick={() => { }} />
      <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl border border-red-100 overflow-hidden modal-enter">
        {step === 1 && (
          <>
            <div className="px-6 py-5 border-b border-gray-100 bg-red-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><FaTrash className="text-red-600 text-lg" /></div>
                <div><h3 className="text-base font-bold text-gray-900">Delete Catalog?</h3><p className="text-xs text-red-600 font-medium">This action cannot be undone.</p></div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">You are about to permanently delete the following catalog data:</p>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-xs font-mono text-gray-700 border border-gray-200">
                <div className="flex justify-between"><span>Rows Indexed:</span><span className="font-bold">{flow.itemsEmbedded}</span></div>
                <div className="flex justify-between"><span>Columns:</span><span className="font-bold">{flow.columnsMapped}</span></div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-100 rounded-lg">
                <FaExclamationTriangle className="text-orange-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-orange-800">All embedding vectors, schema configurations, and linked Google Sheet data will be permanently removed from our servers.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition-all">Cancel</button>
              <button onClick={handleNext} className="px-5 py-2 rounded-xl bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-all shadow-sm">I Understand, Continue</button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Final Confirmation</h3>
              <p className="text-xs text-gray-500 mt-1">Type <span className="font-mono font-bold text-red-600 bg-red-50 px-1 rounded">delete</span> to confirm.</p>
            </div>
            <div className="px-6 py-6">
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition" placeholder="Type 'delete' here..." autoFocus />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition-all">Back</button>
              <button onClick={handleFinalConfirm} disabled={confirmText.toLowerCase() !== "delete"} className="px-5 py-2 rounded-xl bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">Permanently Delete</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CatalogDashboard = ({ flow, recommendationEnabled, catalogStatus, syncOp, reembedOp, needsReembed, syncLogs, onSync, onOpenReembedReview, onConfigure, onDismissSyncError, onDismissReembedError, onTerminalProceed, isAnalyzingForReembed, onRefresh, onDeleteRequest, onToggleRecommendation, recommendationEndpoint, setRecommendationEndpoint, onSaveRecommendation, savingRecommendation, onRestartFlow, localMinSimilarity, setLocalMinSimilarity, localLlmProvider, setLocalLlmProvider, localLinkedId, setLocalLinkedId, localResultColumns, setLocalResultColumns }) => {
  const formatTime = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };
  const isSyncing = syncOp.loading;
  const isReembedding = reembedOp.loading;
  const FRONTEND_MAPPING = {
    title: { label: "Card Title", desc: "Primary header for result cards", keys: ["course name", "course_name", "title", "name"], roles: ["title", "identifier"] },
    subtitle: { label: "Subtitle / Instructor", desc: "Secondary text (e.g., University, Instructor)", keys: ["course_instructor", "instructor", "University", "company", "subtitle"], roles: ["description", "metadata"] },
    image: { label: "Thumbnail Image", desc: "Visual cover for the result card", keys: ["course_thumbnail", "thumbnail", "image", "image_url"], roles: ["media"] },
    price: { label: "Price / Cost", desc: "Displayed as currency value", keys: ["course_cost", "cost", "price"], roles: ["soft_preference"] },
    url: { label: "Action Link", desc: "Destination URL for the 'View' button", keys: ["course_url", "url", "link"], roles: ["metadata", "identifier"] },
    badge: { label: "Badge / Category", desc: "Small tag in top-left corner", keys: ["course_country", "country", "course_category", "category", "course_type"], roles: ["facet"] }
  };
  const getResultColumnValidation = () => {
    if (!flow.columns || flow.columns.length === 0) return { isValid: true, suggestions: [], warnings: [], recommended: [], missingCritical: [] };
    const suggestions = [];
    const warnings = [];
    const recommended = new Set();
    const missingCritical = [];
    const missingOptional = [];
    Object.entries(FRONTEND_MAPPING).forEach(([field, config]) => {
      const hasKeyMatch = flow.columns.some(c => config.keys.some(k => c.name.toLowerCase() === k.toLowerCase()) && localResultColumns.includes(c.name));
      const hasRoleMatch = flow.columns.some(c => config.roles.includes(c.role) && localResultColumns.includes(c.name));
      if (!hasKeyMatch && !hasRoleMatch) {
        const keyCandidate = flow.columns.find(c => config.keys.some(k => c.name.toLowerCase() === k.toLowerCase()));
        const roleCandidate = flow.columns.find(c => config.roles.includes(c.role));
        const bestCandidate = keyCandidate || roleCandidate;
        if (bestCandidate) {
          recommended.add(bestCandidate.name);
          if (field === 'title' || field === 'image') missingCritical.push(config.label);
          else missingOptional.push(config.label);
        }
      }
    });
    if (missingCritical.length > 0) warnings.push(`Missing critical display fields: ${missingCritical.join(", ")}. Cards may look empty.`);
    if (recommended.size > 0) suggestions.push(`We found columns that match your ChatUI design. Select them for best results.`);
    const isValid = localResultColumns.length > 0 && missingCritical.length === 0;
    return { isValid, suggestions, warnings, recommended: Array.from(recommended), missingCritical, missingOptional };
  };
  const validation = getResultColumnValidation();
  const handleAutoSelectRecommended = () => {
    const newSelections = new Set(localResultColumns);
    validation.recommended.forEach(col => newSelections.add(col));
    setLocalResultColumns(Array.from(newSelections));
  };
  return (
    <div className="mt-6 gs-fade-up">
      <div className="bg-white rounded-2xl shadow-sm border border-[#E5DCE7] p-6">
        <div className="flex items-start justify-between mb-6">
          <div><h3 className="text-lg font-bold text-gray-900">Recommendation Engine</h3><p className="text-sm text-gray-500">Manage your endpoint and catalog</p></div>
          <button onClick={onRefresh} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#D4C7D8] text-[#5B3A60] text-xs font-bold hover:bg-[#5B3A60]/5 transition-all" title="Refresh"><FaSync className="text-[10px]" /></button>
        </div>
        <div className="flex items-center justify-between p-4 bg-[#F8F4F2] rounded-xl mb-6">
          <div><label className="text-sm font-bold text-[#4B334F]">Enable Recommendations</label></div>
          <Toggle value={recommendationEnabled} onChange={onToggleRecommendation} disabled={false} />
        </div>
        {recommendationEnabled && (
          <>
            <div className="space-y-4 border-t border-gray-100 pt-6 mb-6">
              <h4 className="text-sm font-bold text-[#4B334F] flex items-center gap-2"><FaCogs className="text-xs" /> Recommender Configuration</h4>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#4B334F]">API Endpoint URL</label>
                <input type="text" value={recommendationEndpoint} onChange={(e) => setRecommendationEndpoint(e.target.value)} className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#5B3A60] outline-none transition" placeholder="https://your-recommendation-api.com" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#4B334F]">Min Similarity (Threshold)</label>
                <input type="number" step="0.01" min="0" max="1" value={localMinSimilarity} onChange={(e) => setLocalMinSimilarity(parseFloat(e.target.value))} className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#5B3A60] outline-none transition" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#4B334F]">LLM Provider</label>
                <select value={localLlmProvider} onChange={(e) => setLocalLlmProvider(e.target.value)} className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#5B3A60] outline-none transition">
                  <option value="openai">OpenAI</option><option value="groq">Groq</option><option value="anthropic">Anthropic</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#4B334F]">Linked ID</label>
                <div className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700">{localLinkedId || "Not Available"}</div>
              </div>
              <div className="flex flex-col gap-3 mt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#4B334F]">Result Columns</label>
                  {validation.recommended.length > 0 && (
                    <button onClick={handleAutoSelectRecommended} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#5B3A60]/10 text-[#5B3A60] text-[10px] font-bold hover:bg-[#5B3A60]/20 transition-colors border border-[#5B3A60]/20"><FaMagic className="text-[8px]" /> Auto-Select for Chat UI</button>
                  )}
                </div>
                {(validation.suggestions.length > 0 || validation.missingCritical.length > 0) && (
                  <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 mb-2 animate-fade-in">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-1.5 bg-blue-100 rounded-lg text-blue-600 mt-0.5"><FaLightbulb className="text-xs" /></div>
                      <div><h5 className="text-xs font-bold text-blue-900">Optimize for Chat UI</h5><p className="text-[10px] text-blue-700 mt-0.5">Based on your frontend design, we recommend including:</p></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-1">
                      {validation.suggestions.map((suggestion, idx) => (
                        <div key={idx} className="bg-white rounded-lg border border-blue-100 p-2.5 shadow-sm flex items-center gap-2"><FaCheckCircle className="text-blue-500 text-[10px]" /><span className="text-[10px] text-gray-700">{suggestion}</span></div>
                      ))}
                    </div>
                    {validation.missingCritical.length > 0 && (
                      <div className="mt-3 px-3 py-2 bg-orange-50 border border-orange-100 rounded-lg flex items-center gap-2"><FaExclamationTriangle className="text-orange-500 text-[10px]" /><span className="text-[10px] text-orange-700 font-medium">Missing Critical Fields: {validation.missingCritical.join(', ')}</span></div>
                    )}
                  </div>
                )}
                <div className="border border-[#D4C7D8] rounded-lg p-3 bg-white max-h-40 overflow-y-auto gs-scroll">
                  {flow.columns && flow.columns.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {flow.columns.map((col) => (
                        <label key={col.name} className={`flex items-center gap-2 cursor-pointer text-xs p-1.5 rounded-md transition-colors ${localResultColumns.includes(col.name) ? 'bg-[#5B3A60]/5' : 'hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={localResultColumns.includes(col.name)} onChange={(e) => { if (e.target.checked) setLocalResultColumns([...localResultColumns, col.name]); else setLocalResultColumns(localResultColumns.filter(c => c !== col.name)); }} className="w-3.5 h-3.5 rounded border-[#D4C7D8] text-[#5B3A60] focus:ring-[#5B3A60]" />
                          <div className="flex flex-col">
                            <span className={`truncate font-medium ${localResultColumns.includes(col.name) ? 'text-[#5B3A60]' : 'text-gray-700'}`} title={col.name}>{col.name}</span>
                            {col.role && col.role !== 'ignore' && (<span className="text-[9px] text-gray-400 capitalize">{col.role.replace('_', ' ')}</span>)}
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (<p className="text-xs text-gray-400 text-center py-2">No columns available. Please finalize onboarding first.</p>)}
                </div>
                {localResultColumns.length > 0 && (
                  <div className="flex items-center gap-2 px-2"><FaCheckCircle className="text-green-500 text-[10px]" /><p className="text-[10px] text-gray-500">{localResultColumns.length} column(s) selected for results</p></div>
                )}
              </div>
              <button onClick={onSaveRecommendation} disabled={!recommendationEndpoint || savingRecommendation} className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#5B3A60] text-white text-sm font-bold hover:bg-[#462B4B] transition-all disabled:opacity-50 disabled:cursor-not-allowed w-fit">
                {savingRecommendation ? <FaSpinner className="gs-spin" /> : <FaSave />} Save Configuration
              </button>
            </div>
            <div className="border-t border-gray-100 pt-6">
              <h4 className="text-sm font-bold text-[#4B334F] mb-4 flex items-center gap-2"><FaDatabase className="text-xs" /> Catalog Management</h4>
              {catalogStatus === "NOT_CONFIGURED" && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                  <FaDatabase className="mx-auto text-gray-400 text-3xl mb-3" />
                  <p className="text-sm font-bold text-gray-700 mb-1">No Catalog Connected</p>
                  <p className="text-xs text-gray-500 mb-4">Connect a Google Sheet to start building your recommendation catalog.</p>
                  <button onClick={onRestartFlow} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#5B3A60] text-white text-sm font-bold hover:bg-[#462B4B] transition-all mx-auto"><FaGoogle className="text-xs" /> Connect Google Sheet</button>
                </div>
              )}
              {(catalogStatus === "ACTIVE" || catalogStatus === "NEEDS_REEMBED") && (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#F8F4F2] rounded-xl p-4 text-center"><div className="text-2xl font-bold text-[#5B3A60] mb-1">{flow.itemsEmbedded}</div><div className="text-xs text-gray-500 font-medium">Total Rows</div></div>
                    <div className="bg-[#F8F4F2] rounded-xl p-4 text-center"><div className="text-2xl font-bold text-[#5B3A60] mb-1">{flow.columnsMapped}</div><div className="text-xs text-gray-500 font-medium">Total Columns</div></div>
                    <div className="bg-[#F8F4F2] rounded-xl p-4 text-center"><div className="text-2xl font-bold text-[#5B3A60] mb-1">{flow.minSimilarity}</div><div className="text-xs text-gray-500 font-medium">Min similarity</div></div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${catalogStatus === "ACTIVE" ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"}`}>
                      <FaCheckCircle className={catalogStatus === "ACTIVE" ? "text-green-600 text-sm" : "text-orange-500 text-sm"} />
                      <span className={`text-xs font-bold ${catalogStatus === "ACTIVE" ? "text-green-700" : "text-orange-700"}`}>{catalogStatus === "ACTIVE" ? "Catalog Active" : "Needs Re-embed"}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-gray-50 border-gray-200"><FaCalendarAlt className="text-gray-500 text-xs" /><span className="text-xs font-bold text-gray-600">Last Synced: {formatTime(flow.lastSynced)}</span></div>
                  </div>
                  {needsReembed && !isSyncing && !isReembedding && !isAnalyzingForReembed && (<div className="mb-4 gs-fade-up"><WarnBanner message="Changes detected in your Google Sheet. Please review and Re-embed to update the catalog." /></div>)}
                  {syncLogs.length > 0 && (<Terminal logs={syncLogs} onProceed={needsReembed ? onTerminalProceed : null} isProceeding={isAnalyzingForReembed} />)}
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <button onClick={needsReembed ? onOpenReembedReview : onSync} disabled={isSyncing || isReembedding || isAnalyzingForReembed} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${needsReembed ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg hover:shadow-orange-500/20 hover:scale-[1.02]" : "bg-[#5B3A60] text-white hover:bg-[#462B4B]"}`}>
                      {isSyncing ? <FaSpinner className="text-xs gs-spin" /> : isReembedding ? <FaSpinner className="text-xs gs-spin" /> : needsReembed ? <FaRedo className="text-xs" /> : <FaSync className="text-xs" />}
                      {isSyncing ? "Syncing..." : isReembedding ? "Re-embedding..." : needsReembed ? "Review & Re-embed" : "Sync Catalog"}
                    </button>
                    <button onClick={onConfigure} disabled={isSyncing || isReembedding || isAnalyzingForReembed} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#D4C7D8] text-[#5B3A60] text-sm font-bold hover:bg-[#5B3A60]/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"><FaCogs className="text-xs" /> Configure View</button>
                  </div>
                  <div className="mt-4 flex justify-center gap-3">
                    <button onClick={onDeleteRequest} disabled={isSyncing || isReembedding} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"><FaTrash className="text-xs" /> Delete Catalog</button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function RecomSystem() {
  const { tenantId, botId } = useParams();
  const [toast, showToast] = useToast();
  const [flow, dispatch] = useReducer(flowReducer, initialFlowState);
  const [gsOpen, setGsOpen] = useState(false);
  const [weightsModalOpen, setWeightsModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [schemaUpdateData, setSchemaUpdateData] = useState(null);
  const [schemaUpdateLoading, setSchemaUpdateLoading] = useState(false);
  const [syncOp, setSyncOp] = useState({ ...initialOpState });
  const [reembedOp, setReembedOp] = useState({ ...initialOpState });
  const [needsReembed, setNeedsReembed] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showReembedReview, setShowReembedReview] = useState(false);
  const [awaitingTerminalProceedState, setAwaitingTerminalProceedState] = useState(false);
  const [isAnalyzingForReembed, setIsAnalyzingForReembed] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localRecEndpoint, setLocalRecEndpoint] = useState("");
  const [localMinSimilarity, setLocalMinSimilarity] = useState(0.4);
  const [localLlmProvider, setLocalLlmProvider] = useState("openai");
  const [localLinkedId, setLocalLinkedId] = useState("");
  const [localResultColumns, setLocalResultColumns] = useState([]);
  const [savingRecEndpoint, setSavingRecEndpoint] = useState(false);
  const [recommendationEnabled, setRecommendationEnabled] = useState(false);
  const [catalogStatus, setCatalogStatus] = useState("NOT_CONFIGURED");

  const finalizeInProgress = useRef(false);
  const analyzeAbortRef = useRef(null);
  const previewAbortRef = useRef(null);
  const syncStageTimerRef = useRef(null);
  const reembedStageTimerRef = useRef(null);
  const analyzeTimerRef = useRef(null);

  const advanceStages = (setOp, stages, intervalMs = 4000) => {
    let idx = 0;
    const tick = () => {
      idx = Math.min(idx + 1, stages.length - 1);
      setOp(prev => ({ ...prev, stageIdx: idx, progress: stages[idx].pct }));
    };
    return setInterval(tick, intervalMs);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) return;
        const data = await res.json();
        if (!res.ok) throw new Error("Failed");
        const recommender = data?.configuration?.tools?.recommender || {};
        const hasCatalogData = recommender.sheet_id || recommender.source_config?.worksheet;

        setRecommendationEnabled(recommender.enabled ?? false);
        setCatalogStatus(recommender.catalog_status || (hasCatalogData ? "ACTIVE" : "NOT_CONFIGURED"));
        setLocalRecEndpoint(recommender.endpoint || "");
        setLocalMinSimilarity(recommender.min_similarity ?? 0.4);
        setLocalLlmProvider(recommender.llm_provider || "openai");
        const accountLabel = recommender.account_info?.account_label || recommender.accounts?.[0]?.account_label || "";
        setLocalLinkedId(accountLabel || recommender.linked_id || "");
        const savedResultColumns = recommender.result_columns;
        if (Array.isArray(savedResultColumns) && savedResultColumns.length > 0) setLocalResultColumns(savedResultColumns);

        if (hasCatalogData || recommender.enabled) {
          let mappedGoogleUser = null;
          if (recommender.account_info && recommender.account_info.account_label) mappedGoogleUser = { email: recommender.account_info.account_label };
          else if (recommender.accounts && recommender.accounts.length > 0) mappedGoogleUser = { email: recommender.accounts[0].account_label };

          dispatch({
            type: "UPDATE_DASHBOARD_STATS",
            itemsEmbedded: recommender.items_embedded || recommender.row_count || 0,
            columnsMapped: recommender.columns_mapped || recommender.column_count || 0,
            minSimilarity: recommender.min_similarity || 0,
            lastSynced: recommender.last_synced || recommender.last_finalized_at || null,
          });
          dispatch({
            type: "SCHEMA_FOUND",
            config: {
              sheet_id: recommender.sheet_id,
              sheet_name: recommender.sheet_name || recommender.source_config?.worksheet || "Saved Catalog",
              source_config: recommender.source_config || {},
              embed_template: recommender.embed_template || "",
              scoring_weights: recommender.scoring_weights || {},
              column_mapping: recommender.column_mapping || recommender.source_config?.column_mapping || {},
              items_embedded: recommender.items_embedded || recommender.row_count || 0,
              columns_mapped: recommender.columns_mapped || recommender.column_count || 0,
              min_similarity: recommender.min_similarity || 0,
              last_synced: recommender.last_synced || recommender.last_finalized_at || null,
              google_user: mappedGoogleUser,
              status: recommender.status,
            },
          });
          if (recommender.enabled) setGsOpen(false);
        }
      } catch (err) {
        console.error("Load recom config error:", err);
      }
    };
    load();
  }, [tenantId, botId]);

  useEffect(() => {
    if (window.gapi) return;
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.onload = () => window.gapi?.load("picker", () => { });
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    return () => {
      if (syncStageTimerRef.current) clearInterval(syncStageTimerRef.current);
      if (reembedStageTimerRef.current) clearInterval(reembedStageTimerRef.current);
      if (analyzeTimerRef.current) clearTimeout(analyzeTimerRef.current);
    };
  }, []);

  const dismissSyncError = () => setSyncOp({ ...initialOpState });
  const dismissReembedError = () => setReembedOp({ ...initialOpState });
  const handleRefresh = () => window.location.reload();

  const handleDeleteCatalog = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await fetch(`${getRecomApiBase()}/onboard/catalog`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
      });
      if (res.ok) {
        try {
          await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tools: { recommender: { result_columns: null } } })
          });
        } catch (configErr) { console.warn("Error clearing result_columns:", configErr); }
        showToast("success", "Catalog deleted successfully.");
        dispatch({ type: "RESET_FLOW" });
        setShowDeleteConfirm(false);
        setLocalResultColumns([]);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const errData = await res.json();
        showToast("error", errData.detail || "Failed to delete catalog");
      }
    } catch (err) {
      showToast("error", "Network error during deletion");
    }
  };

  const loadPickerSDK = useCallback(() =>
    new Promise((resolve, reject) => {
      if (!window.gapi) { reject(new Error("gapi not loaded")); return; }
      window.gapi.load("picker", { callback: resolve, onerror: () => reject(new Error("Picker SDK failed")) });
    }), []);

  const openGooglePicker = useCallback(async (accessToken) => {
    if (!window.google?.picker) { dispatch({ type: "PICKER_SDK_FAILED" }); return; }
    dispatch({ type: "PICKER_OPEN" });
    const view = new window.google.picker.View(window.google.picker.ViewId.SPREADSHEETS);
    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setDeveloperKey("AIzaSyAvYLeD2Dme0IbsjwdO7hJ01c6L-rg4Pug")
      .setCallback(async (pickerData) => {
        if (pickerData.action === window.google.picker.Action.PICKED) {
          const doc = pickerData.docs[0];
          try {
            const res = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${doc.id}`, { headers: { Authorization: `Bearer ${accessToken}` } });
            if (res.status === 429) { dispatch({ type: "SHEET_QUOTA_EXCEEDED" }); return; }
            if (!res.ok) throw new Error("Failed to fetch sheet metadata");
            const data = await res.json();
            const tabs = (data.sheets || []).map((s) => s.properties.title);
            if (!tabs.length) { dispatch({ type: "SHEET_NO_TABS" }); return; }
            dispatch({ type: "SHEET_SELECTED", sheetMeta: { id: doc.id, name: doc.name, tabs }, sheetId: doc.id, defaultTab: tabs[0] });
          } catch (err) {
            dispatch({ type: "GOOGLE_AUTH_FAILED", error: "Failed to read spreadsheet: " + err.message });
          }
        } else if (pickerData.action === window.google.picker.Action.CANCEL || pickerData.action === "cancel") {
          dispatch({ type: "PICKER_CANCEL" });
        }
      })
      .build();
    picker.setVisible(true);
  }, []);

  const startGoogleFlow = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/spreadsheets.readonly https://www.googleapis.com/auth/drive.readonly",
    flow: "auth-code",
    usePopup: true,
    onSuccess: async (response) => {
      try {
        const res = await fetch(`${getApiBase()}/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: response.code, tenant_id: tenantId }),
        });
        let tokens;
        try { tokens = await res.json(); } catch {
          dispatch({ type: "GOOGLE_AUTH_FAILED", error: "Authentication server returned invalid response." });
          return;
        }
        if (!res.ok || tokens.status === "error") {
          dispatch({ type: "GOOGLE_AUTH_FAILED", error: tokens.message || "Backend authentication failed." });
          return;
        }
        const accessToken = tokens.access_token;
        if (!accessToken) { dispatch({ type: "GOOGLE_AUTH_FAILED", error: "No access token received." }); return; }
        let user;
        try {
          const userRes = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", { headers: { Authorization: `Bearer ${accessToken}` } });
          if (!userRes.ok) throw new Error("Failed to fetch user");
          user = await userRes.json();
        } catch {
          dispatch({ type: "GOOGLE_AUTH_FAILED", error: "Could not fetch Google user info." });
          return;
        }
        dispatch({ type: "GOOGLE_AUTH_SUCCESS", token: accessToken, user });
        dispatch({ type: "CHECKING_SCHEMA" });
        try {
          const token = localStorage.getItem("token");
          const schemaRes = await fetch(`${getRecomApiBase()}/onboard/schema-status`, {
            headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
          });
          const schemaData = await schemaRes.json();
          if (schemaData.exists) {
            const existingRes = await fetch(`${getRecomApiBase()}/onboard/schema?user_email=${encodeURIComponent(user.email)}`, { headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId } });
            const existingSchema = await existingRes.json();
            dispatch({ type: "SCHEMA_FOUND", config: { ...schemaData, ...existingSchema, google_user: user } });
            const sheetUrl = `https://docs.google.com/spreadsheets/d/${schemaData.sheet_id}`;
            dispatch({ type: "ANALYZE_START" });
            const analyzeRes = await fetch(`${getRecomApiBase()}/onboard/analyze`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
              body: JSON.stringify({ user_id: user.email, sheet_url: sheetUrl, worksheet: schemaData.source_config?.worksheet, domain_hint: "education" }),
            });
            const analyzeData = await analyzeRes.json();
            if (!analyzeRes.ok) { dispatch({ type: "ANALYZE_FAILED", error: analyzeData.detail || "Analyze failed" }); return; }
            const formatted = (analyzeData.draft_roles || []).map((col) => ({
              name: col.name || col.dimension,
              role: col.role,
              originalRole: col.role,
              manuallyModified: false,
              type: col.type || analyzeData.type_info?.[col.name || col.dimension]?.type || "text",
            }));
            dispatch({
              type: "ANALYZE_SUCCESS",
              columns: formatted,
              roleOptions: ["identifier", "title", "description", "semantic", "hard_filter", "soft_preference", "facet", "media", "metadata", "ignore"],
              embedTemplate: existingSchema.embed_template || "",
              scoringWeights: existingSchema.scoring_weights || {},
            });
            setGsOpen(true);
            return;
          }
          dispatch({ type: "PICKER_SDK_LOADING" });
          await loadPickerSDK();
          openGooglePicker(accessToken);
        } catch {
          dispatch({ type: "GOOGLE_AUTH_FAILED", error: "Failed to check schema" });
        }
      } catch {
        dispatch({ type: "GOOGLE_AUTH_FAILED", error: "Network error during authentication." });
      }
    },
    onError: (error) => {
      const cancelled = error?.error === "access_denied" || error?.error === "popup_closed_by_user" || error?.error_description?.toLowerCase().includes("cancel");
      const blocked = error?.error === "popup_failed_to_open" || error?.error_description?.toLowerCase().includes("popup");
      if (cancelled) dispatch({ type: "GOOGLE_CANCEL" });
      else if (blocked) dispatch({ type: "GOOGLE_POPUP_BLOCKED" });
      else dispatch({ type: "GOOGLE_AUTH_FAILED", error: error?.error_description || "Google login failed." });
    },
  });

  const handleAnalyze = useCallback(async () => {
    if (!flow.googleUser?.email || !flow.sheetMeta || !flow.selectedTab) return;
    if (analyzeAbortRef.current) analyzeAbortRef.current.abort();
    analyzeAbortRef.current = new AbortController();
    if (!flow.sheetMeta.id || flow.sheetMeta.id.length < 20) {
      dispatch({ type: "ANALYZE_FAILED", error: "Invalid sheet ID. Please re-select the spreadsheet." });
      return;
    }
    dispatch({ type: "ANALYZE_START" });
    const timeoutId = setTimeout(() => analyzeAbortRef.current?.abort(), 60000);
    const progressInterval = setInterval(() => {
      dispatch((prev) => ({ ...prev, embeddingProgress: Math.min(95, prev.embeddingProgress + Math.random() * 15) }));
    }, 800);
    try {
      const token = localStorage.getItem("token");
      const sheetUrl = `https://docs.google.com/spreadsheets/d/${flow.sheetMeta.id}?t=${Date.now()}`;
      const res = await fetch(`${getRecomApiBase()}/onboard/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
        signal: analyzeAbortRef.current.signal,
        body: JSON.stringify({ user_id: flow.googleUser.email, sheet_url: sheetUrl, worksheet: flow.selectedTab, domain_hint: "education" }),
      });
      clearTimeout(timeoutId);
      clearInterval(progressInterval);
      if (res.status === 401) { dispatch({ type: "ANALYZE_TOKEN_EXPIRED" }); return; }
      let data;
      try { data = await res.json(); } catch {
        dispatch({ type: "ANALYZE_FAILED", error: "Server returned an invalid response." });
        return;
      }
      if (!res.ok) { dispatch({ type: "ANALYZE_FAILED", error: data.detail || "Analysis failed." }); return; }
      const draftRoles = data.draft_roles || [];
      if (!draftRoles.length) { dispatch({ type: "ANALYZE_FAILED", error: "No columns were detected in this worksheet." }); return; }
      const roleOptions = ["identifier", "title", "description", "semantic", "hard_filter", "soft_preference", "facet", "media", "metadata", "ignore"];
      const formatted = draftRoles.map((col) => ({
        name: col.name || col.dimension,
        role: col.role,
        originalRole: col.role,
        manuallyModified: false,
        type: col.type || data.type_info?.[col.name || col.dimension]?.type || "text",
      }));
      const activeForEmbed = formatted.filter((c) => c.role !== "ignore");
      if (!activeForEmbed.length) { dispatch({ type: "ANALYZE_FAILED", error: "All columns are set to ignore. No usable columns detected." }); return; }
      const tmpl = activeForEmbed.map((c) => `{${c.name}}`).join(" ");
      dispatch({ type: "ANALYZE_SUCCESS", columns: formatted, roleOptions, embedTemplate: tmpl, scoringWeights: data.scoring_weights || {} });
    } catch (err) {
      clearTimeout(timeoutId);
      clearInterval(progressInterval);
      if (err.name === "AbortError") dispatch({ type: "ANALYZE_FAILED", error: "Analysis timed out. Please try again." });
      else dispatch({ type: "ANALYZE_FAILED", error: err.message || "Analysis failed." });
    }
  }, [flow.googleUser, flow.sheetMeta, flow.selectedTab, tenantId, botId]);

  const handlePreview = useCallback(async () => {
    if (previewAbortRef.current) previewAbortRef.current.abort();
    previewAbortRef.current = new AbortController();
    const activeColumns = flow.columns.filter((c) => c.role !== "ignore");
    if (!activeColumns.length) {
      showToast("error", "All columns are ignored. Assign at least one column a role before previewing.");
      return;
    }
    const invalidPlaceholders = validateTemplatePlaceholders(flow.embedTemplate, flow.columns);
    if (invalidPlaceholders.length) {
      dispatch({ type: "EMBED_TEMPLATE_CHANGE", template: flow.embedTemplate, invalidPlaceholders });
      showToast("error", `Invalid placeholders in template: ${invalidPlaceholders.join(", ")}`);
      return;
    }
    dispatch({ type: "PREVIEW_START" });
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getRecomApiBase()}/onboard/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId },
        signal: previewAbortRef.current.signal,
        body: JSON.stringify({
          user_id: flow.googleUser.email,
          sheet_url: `https://docs.google.com/spreadsheets/d/${flow.selectedSheet}`,
          worksheet: flow.selectedTab,
          header_row: 1,
          approved_roles: Object.fromEntries(activeColumns.map((c) => [c.name, c.role])),
          embed_template: flow.embedTemplate,
          n_samples: 5,
        }),
      });
      let data;
      try { data = await res.json(); } catch {
        dispatch({ type: "PREVIEW_FAILED", error: "Server returned an invalid response." });
        return;
      }
      if (!res.ok) { dispatch({ type: "PREVIEW_FAILED", error: data.detail || "Preview failed." }); return; }
      dispatch({ type: "PREVIEW_SUCCESS", samples: data.samples || [] });
    } catch (err) {
      if (err.name === "AbortError") return;
      dispatch({ type: "PREVIEW_FAILED", error: err.message || "Preview failed." });
    }
  }, [flow.columns, flow.embedTemplate, flow.googleUser, tenantId]);

  const handleFinalize = useCallback(async () => {
    if (finalizeInProgress.current) { showToast("info", "Embedding is already in progress."); return; }
    const activeColumns = flow.columns.filter((c) => c.role !== "ignore");
    if (!activeColumns.length) { showToast("error", "No active columns to embed."); return; }
    if (!flow.selectedSheet || flow.selectedSheet.length < 20) { showToast("error", "Invalid sheet ID. Please re-select the spreadsheet."); return; }
    finalizeInProgress.current = true;
    dispatch({ type: "EMBED_START" });
    const progressInterval = setInterval(() => {
      dispatch({ type: "EMBED_PROGRESS", progress: Math.min(95, flow.embeddingProgress + 5) });
    }, 500);
    EMBED_STAGES.forEach((_, i) => { setTimeout(() => { dispatch({ type: "EMBED_STAGE", stage: i }); }, i * 1400); });
    try {
      const token = localStorage.getItem("token");
      const sheetUrl = `https://docs.google.com/spreadsheets/d/${flow.selectedSheet}`;
      const finalRoles = {};
      const finalWeights = {};
      activeColumns.forEach((col) => {
        finalRoles[col.name] = col.role;
        const isScoringRole = col.role === "hard_filter" || col.role === "soft_preference";
        if (!isScoringRole) return;
        finalWeights[col.name] = flow.scoringWeights[col.name] ?? (col.role === "hard_filter" ? 0.2 : 0.1);
      });
      if (flow.scoringWeights.semantic_relevance !== undefined) finalWeights.semantic_relevance = flow.scoringWeights.semantic_relevance;
      const res = await fetch(`${getRecomApiBase()}/onboard/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
        body: JSON.stringify({
          user_id: flow.googleUser.email,
          sheet_url: sheetUrl,
          worksheet: flow.selectedTab,
          header_row: 1,
          approved_roles: finalRoles,
          embed_template: flow.embedTemplate,
          scoring_weights: finalWeights,
        }),
      });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      clearInterval(progressInterval);
      dispatch({ type: "EMBED_PROGRESS", progress: 100 });
      await new Promise((r) => setTimeout(r, 600));
      if (res.ok) {
        dispatch({ type: "EMBED_SUCCESS", itemsEmbedded: data.items_embedded, columnsMapped: activeColumns.length, minSimilarity: data.min_similarity });
        showToast("success", "Embedding completed successfully.");
        setTimeout(() => window.location.reload(), 2500);
      } else {
        dispatch({ type: "EMBED_FAILED", error: data.detail || "Finalize failed. Please try again." });
      }
    } catch (err) {
      clearInterval(progressInterval);
      await new Promise((r) => setTimeout(r, 600));
      dispatch({ type: "EMBED_FAILED", error: err.message || "Embedding failed." });
    } finally {
      finalizeInProgress.current = false;
    }
  }, [flow, tenantId, botId]);

  const handleSaveRecommendationEndpoint = async () => {
    if (!localRecEndpoint) {
      showToast("error", "Please enter a valid endpoint URL");
      return;
    }
    setSavingRecEndpoint(true);
    try {
      const token = localStorage.getItem("token");
      const parsedResultColumns = localResultColumns.length > 0 ? localResultColumns : null;
      const payload = {
        tools: {
          recommender: {
            enabled: true,
            endpoint: localRecEndpoint,
            min_similarity: localMinSimilarity,
            llm_provider: localLlmProvider,
            result_columns: parsedResultColumns,
            linked_id: localLinkedId
          }
        }
      };
      const response = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update");
      const result = await response.json();
      if (result?.status === "success") {
        setRecommendationEnabled(true);
        showToast("success", "Recommender configuration saved successfully");
      } else {
        showToast("error", result?.message || "Update failed");
      }
    } catch (err) {
      showToast("error", "Failed to save configuration");
    } finally {
      setSavingRecEndpoint(false);
    }
  };

  const handleToggleRecommendation = async (enabled) => {
    setRecommendationEnabled(enabled);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        tools: {
          recommender: {
            enabled,
            endpoint: localRecEndpoint || "",
            min_similarity: localMinSimilarity,
            llm_provider: localLlmProvider,
            result_columns: localResultColumns.length > 0 ? localResultColumns : null,
            linked_id: localLinkedId,
          },
        },
      };
      const response = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to save recommendation setting");
      showToast("success", `Recommendations ${enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      showToast("error", "Failed to save recommendation setting");
      setRecommendationEnabled(!enabled);
    }
  };

  const handleRestartFlow = () => {
    dispatch({ type: "RESET_FLOW" });
    setGsOpen(true);
    dispatch({ type: "GOOGLE_START" });
    startGoogleFlow();
  };

  const updateColumnRole = (i, role) => {
    const updated = [...flow.columns];
    const previousRole = updated[i].role;
    updated[i] = { ...updated[i], role, manuallyModified: role !== updated[i].originalRole };
    let weights = { ...flow.scoringWeights };
    const isScoringRole = role === "hard_filter" || role === "soft_preference";
    const wasScoringRole = previousRole === "hard_filter" || previousRole === "soft_preference";
    if (!isScoringRole && wasScoringRole) delete weights[updated[i].name];
    if (isScoringRole && updated[i].manuallyModified) weights[updated[i].name] = weights[updated[i].name] ?? (role === "hard_filter" ? 0.2 : 0.1);
    if (!updated[i].manuallyModified && !flow.scoringWeights[updated[i].name]) delete weights[updated[i].name];
    const allIgnored = updated.every((c) => c.role === "ignore");
    dispatch({ type: "COLUMN_ROLE_CHANGE", columns: updated, scoringWeights: weights, allColumnsIgnored: allIgnored });
  };

  const handleTemplateChange = (template) => {
    const invalidPlaceholders = validateTemplatePlaceholders(template, flow.columns);
    dispatch({ type: "EMBED_TEMPLATE_CHANGE", template, invalidPlaceholders });
  };

  const getWeightSummary = () => {
    const entries = Object.entries(flow.scoringWeights);
    if (!entries.length) return "No weights configured";
    return `${entries.length} dimension(s)`;
  };

  const handleSync = async () => {
    const userEmail = flow.googleUser?.email || flow.existingConfig?.google_user?.email;
    if (!userEmail) { showToast("error", "No Google account linked. Please reconnect to sync."); return; }
    if (!flow.selectedSheet && !flow.existingConfig?.sheet_id) { showToast("error", "No sheet linked. Complete onboarding first."); return; }
    if (syncOp.loading || reembedOp.loading) { showToast("info", "Another operation is already in progress."); return; }
    setSyncLogs([]);
    setSyncOp({ loading: true, stageIdx: 0, progress: 0, error: null, done: false });
    setNeedsReembed(false);
    setAwaitingTerminalProceedState(false);
    setIsAnalyzingForReembed(false);
    const addLog = (msg, type = "info") => setSyncLogs(prev => [...prev, { message: msg, type }]);
    addLog("Initializing sync process...");
    await new Promise(r => setTimeout(r, 800));
    addLog("Authenticating with Google Sheets API...");
    await new Promise(r => setTimeout(r, 1200));
    addLog("Fetching latest row count and schema structure...");
    await new Promise(r => setTimeout(r, 1500));
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getRecomApiBase()}/onboard/schema-update/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
        body: JSON.stringify({
          user_id: userEmail,
          sheet_url: `https://docs.google.com/spreadsheets/d/${flow.selectedSheet || flow.existingConfig?.sheet_id}`,
        }),
      });
      if (res.status === 401 || res.status === 403) {
        setSyncOp({ loading: false, stageIdx: 0, progress: 0, error: "Session expired. Please reconnect your Google account.", done: false });
        addLog("Session expired.", "error");
        return;
      }
      if (res.status === 429) {
        setSyncOp({ loading: false, stageIdx: 0, progress: 0, error: "Google API rate limit hit.", done: false });
        addLog("Rate limit hit.", "error");
        return;
      }
      if (!res.ok) {
        let errMsg = "Sync failed.";
        try { const errData = await res.json(); errMsg = errData.detail || errMsg; } catch { }
        setSyncOp({ loading: false, stageIdx: 0, progress: 0, error: errMsg, done: false });
        addLog(errMsg, "error");
        return;
      }
      const data = await res.json();
      const newColsCount = data.new_columns?.length || 0;
      const removedColsCount = data.removed_columns?.length || 0;
      const rowCountDiff = (data.analyzed_row_count || 0) - (flow.itemsEmbedded || 0);
      addLog("Analysis complete. Comparing with local schema...");
      await new Promise(r => setTimeout(r, 800));
      if (!data.requires_schema_update && rowCountDiff === 0) {
        setSyncOp({ loading: false, stageIdx: 0, progress: 100, error: null, done: true });
        addLog("Catalog is already up to date.", "info");
        setTimeout(() => { setSyncOp({ ...initialOpState }); setSyncLogs([]); }, 3000);
        showToast("success", "Catalog already up to date");
        return;
      }
      if (rowCountDiff !== 0) { addLog(`Detected ${rowCountDiff > 0 ? '+' : ''}${rowCountDiff} row changes.`, "warning"); await new Promise(r => setTimeout(r, 600)); }
      if (newColsCount > 0) { addLog(`Detected ${newColsCount} new column(s): ${data.new_columns.map(c => c.name).join(', ')}`, "warning"); await new Promise(r => setTimeout(r, 600)); }
      if (removedColsCount > 0) { addLog(`Detected ${removedColsCount} removed column(s): ${data.removed_columns.join(', ')}`, "warning"); await new Promise(r => setTimeout(r, 600)); }
      addLog("Changes require re-embedding to update vector store.", "action");
      setSyncOp({ loading: false, stageIdx: 0, progress: 100, error: null, done: true });
      setSchemaUpdateData(data);
      setNeedsReembed(true);
      setAwaitingTerminalProceedState(true);
    } catch (err) {
      const msg = err.message?.includes("Failed to fetch") ? "Network error." : err.message || "Sync failed.";
      setSyncOp({ loading: false, stageIdx: 0, progress: 0, error: msg, done: false });
      addLog(msg, "error");
    }
  };

  const handleTerminalProceed = () => {
    setAwaitingTerminalProceedState(false);
    setIsAnalyzingForReembed(true);
    if (analyzeTimerRef.current) clearTimeout(analyzeTimerRef.current);
    analyzeTimerRef.current = setTimeout(() => {
      setIsAnalyzingForReembed(false);
      setShowReembedReview(true);
    }, 5000);
  };

  const handleConfirmReembed = async () => {
    if (!schemaUpdateData) return;
    const userEmail = flow.googleUser?.email || flow.existingConfig?.google_user?.email;
    if (!userEmail) { showToast("error", "No Google account linked."); return; }
    const invalidPlaceholders = validateTemplatePlaceholders(flow.embedTemplate, flow.columns);
    if (invalidPlaceholders.length) {
      showToast("error", `Invalid template placeholders: ${invalidPlaceholders.join(", ")}`);
      return;
    }
    setSchemaUpdateLoading(true);
    try {
      const token = localStorage.getItem("token");
      setReembedOp({ loading: true, stageIdx: 0, progress: 10, error: null, done: false });
      setShowReembedReview(false);
      const updatedColumnsPayload = flow.columns.filter(c => c.role !== 'ignore').map(c => ({ name: c.name, role: c.role }));
      const res = await fetch(`${getRecomApiBase()}/onboard/schema-update/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId },
        body: JSON.stringify({
          user_id: userEmail,
          updated_columns: updatedColumnsPayload,
          removed_columns: schemaUpdateData.removed_columns,
          embed_template: flow.embedTemplate,
          scoring_weights: flow.scoringWeights,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: data.detail || "Re-embed failed", done: false });
        showToast("error", data.detail || "Re-embed failed");
        return;
      }
      setReembedOp({ loading: false, stageIdx: 0, progress: 100, error: null, done: true });
      setTimeout(() => setReembedOp({ ...initialOpState }), 3000);
      dispatch({ type: "UPDATE_DASHBOARD_STATS", itemsEmbedded: data.items_embedded || flow.itemsEmbedded, lastSynced: new Date().toISOString() });
      setNeedsReembed(false);
      setSchemaUpdateData(null);
      showToast("success", "Catalog updated and re-embedded successfully.");
      setTimeout(() => window.location.reload(), 2500);
    } catch (err) {
      setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: err.message, done: false });
      showToast("error", err.message || "Re-embed failed.");
    } finally {
      setSchemaUpdateLoading(false);
    }
  };

  const handleReembed = async () => {
    const userEmail = flow.googleUser?.email || flow.existingConfig?.google_user?.email;
    if (!userEmail) { showToast("error", "No Google account linked. Cannot re-embed."); return; }
    if (syncOp.loading || reembedOp.loading) { showToast("info", "Another operation is already in progress."); return; }
    if (reembedStageTimerRef.current) clearInterval(reembedStageTimerRef.current);
    setReembedOp({ loading: true, stageIdx: 0, progress: REEMBED_STAGES[0].pct, error: null, done: false });
    reembedStageTimerRef.current = advanceStages(setReembedOp, REEMBED_STAGES, 3000);
    try {
      const token = localStorage.getItem("token");
      const statusRes = await fetch(`${getRecomApiBase()}/onboard/schema-status`, { headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId, "X-Bot-ID": botId } });
      if (statusRes.status === 401 || statusRes.status === 403) {
        clearInterval(reembedStageTimerRef.current);
        setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: "Session expired. Please reload the page.", done: false });
        return;
      }
      if (!statusRes.ok) {
        clearInterval(reembedStageTimerRef.current);
        setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: "Could not verify catalog status.", done: false });
        return;
      }
      const statusData = await statusRes.json();
      if (!statusData.exists) {
        clearInterval(reembedStageTimerRef.current);
        setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: "No catalog found. Complete onboarding first.", done: false });
        return;
      }
      const schemaRes = await fetch(`${getRecomApiBase()}/onboard/schema?user_email=${encodeURIComponent(userEmail)}`, { headers: { Authorization: `Bearer ${token}`, "X-Tenant-ID": tenantId } });
      if (!schemaRes.ok) {
        clearInterval(reembedStageTimerRef.current);
        setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: "Failed to load schema configuration.", done: false });
        return;
      }
      const schemaData = await schemaRes.json();
      clearInterval(reembedStageTimerRef.current);
      setReembedOp({ ...initialOpState });
      const storedColumnMapping = schemaData.column_mapping || {};
      const originalCols = Object.keys(storedColumnMapping).length > 0
        ? Object.entries(storedColumnMapping).map(([name, role]) => ({ name, role, originalRole: role, manuallyModified: false, type: "text" }))
        : flow.columns;
      dispatch({
        type: "REEMBED_MODE_INIT",
        originalColumns: originalCols,
        originalTemplate: schemaData.embed_template || flow.embedTemplate,
        originalWeights: schemaData.scoring_weights || flow.scoringWeights,
        storedRowCount: schemaData.row_count || flow.itemsEmbedded,
        analyzedRowCount: schemaData.row_count || flow.itemsEmbedded,
      });
      const sheetId = statusData.sheet_id || flow.selectedSheet;
      const worksheet = statusData.sheet_name || flow.selectedTab;
      dispatch({ type: "SHEET_SELECTED", sheetMeta: { id: sheetId, name: flow.sheetMeta?.name || "Existing Sheet", tabs: [worksheet] }, sheetId, defaultTab: worksheet });
      dispatch({
        type: "ANALYZE_SUCCESS",
        columns: originalCols,
        roleOptions: ["identifier", "title", "description", "semantic", "hard_filter", "soft_preference", "facet", "media", "metadata", "ignore"],
        embedTemplate: schemaData.embed_template || flow.embedTemplate,
        scoringWeights: schemaData.scoring_weights || flow.scoringWeights,
      });
      setGsOpen(true);
      dispatch({ type: "STEP_CHANGE", step: 4 });
    } catch (err) {
      clearInterval(reembedStageTimerRef.current);
      const msg = err.message?.includes("Failed to fetch") ? "Network error. Check your connection and try again." : err.message || "Failed to load catalog configuration.";
      setReembedOp({ loading: false, stageIdx: 0, progress: 0, error: msg, done: false });
    }
  };

  const isEmbedded = flow.phase === FS.EMBEDDED;
  const isEmbedding = flow.phase === FS.EMBEDDING;
  const isAnalyzing = flow.phase === FS.ANALYZING;
  const isPreviewing = flow.phase === FS.PREVIEWING;
  const isCheckingSchema = flow.phase === FS.CHECKING_SCHEMA;
  const isLoadingPickerSDK = flow.phase === FS.PICKER_SDK_LOADING;
  const isPickerOpen = flow.phase === FS.PICKER_OPEN;
  const isConnecting = flow.phase === FS.GOOGLE_CONNECTING;
  const isAnalyzed = [FS.ANALYZED, FS.PREVIEWING, FS.PREVIEW_FAILED, FS.PREVIEWED, FS.EMBEDDING, FS.EMBED_FAILED].includes(flow.phase);
  const isPreviewed = [FS.PREVIEWED, FS.EMBEDDING, FS.EMBED_FAILED].includes(flow.phase);
  const hasError = !!flow.error;

  const getModalLoadingContent = () => {
    if (isConnecting) return <LoadingStep icon={FaGoogle} title="Connecting to Google" subtitle="A popup window will appear for you to sign in. Please allow popups if prompted." />;
    if (isCheckingSchema) return <LoadingStep icon={FaDatabase} title="Checking Your Catalog" subtitle="Looking for an existing recommendation catalog linked to your account..." progress />;
    if (isLoadingPickerSDK) return <LoadingStep icon={SiGooglesheets} title="Loading Sheet Picker" subtitle="Preparing the Google Sheets selector, just a moment..." />;
    if (isPickerOpen) return <LoadingStep icon={SiGooglesheets} title="Select Your Sheet" subtitle="The Google Sheets picker is open. Choose the spreadsheet you'd like to use." />;
    if (isAnalyzing) {
      return (
        <div className="py-4 gs-fade-up">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#5B3A60]/10 flex items-center justify-center mb-3 gs-pulse"><FaDatabase className="text-[#5B3A60] text-xl" /></div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">Analyzing Sheet Structure</h3>
            <p className="text-xs text-gray-500">Detecting column types and data patterns...</p>
          </div>
          <div className="space-y-2.5 mb-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="gs-shimmer h-3" style={{ width: 90, animationDelay: `${i * 0.1}s` }} />
                <div className="gs-shimmer h-3 flex-1" style={{ animationDelay: `${i * 0.15}s` }} />
                <div className="gs-shimmer h-3" style={{ width: 60, animationDelay: `${i * 0.2}s` }} />
                <div className="gs-shimmer h-3" style={{ width: 70, animationDelay: `${i * 0.25}s` }} />
              </div>
            ))}
          </div>
          <div className="bg-gray-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full transition-all duration-300 ease-out" style={{ width: `${Math.min(flow.embeddingProgress, 100)}%` }} /></div>
          <p className="text-center text-[10px] text-gray-400 mt-2">Scanning worksheet: {flow.selectedTab}</p>
        </div>
      );
    }
    return null;
  };

  const modalLoadingContent = getModalLoadingContent();
  const showModalLoading = !!modalLoadingContent && flow.gsStep === 1;

  return (
    <div className="max-w-5xl mx-auto mt-8">
      <style>{shimmerCSS}</style>
      {toast && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <div className={`px-6 py-3 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2 border transition-all ${toast.type === "success" ? "bg-[#5B3A60]/95 text-white border-[#5B3A60]/40" : toast.type === "error" ? "bg-red-500/90 text-white border-red-400" : "bg-[#5B3A60]/90 text-white border-[#5B3A60]/40"}`}>
            {toast.msg}
          </div>
        </div>
      )}
      <CatalogDashboard
        flow={flow}
        recommendationEnabled={recommendationEnabled}
        catalogStatus={catalogStatus}
        syncOp={syncOp}
        reembedOp={reembedOp}
        needsReembed={needsReembed}
        syncLogs={syncLogs}
        onSync={handleSync}
        onOpenReembedReview={() => setShowReembedReview(true)}
        onConfigure={() => setConfigModalOpen(true)}
        onDismissSyncError={dismissSyncError}
        onDismissReembedError={dismissReembedError}
        onTerminalProceed={handleTerminalProceed}
        isAnalyzingForReembed={isAnalyzingForReembed}
        onRefresh={handleRefresh}
        onDeleteRequest={() => setShowDeleteConfirm(true)}
        onToggleRecommendation={handleToggleRecommendation}
        recommendationEndpoint={localRecEndpoint}
        setRecommendationEndpoint={setLocalRecEndpoint}
        onSaveRecommendation={handleSaveRecommendationEndpoint}
        savingRecommendation={savingRecEndpoint}
        onRestartFlow={handleRestartFlow}
        localMinSimilarity={localMinSimilarity}
        setLocalMinSimilarity={setLocalMinSimilarity}
        localLlmProvider={localLlmProvider}
        setLocalLlmProvider={setLocalLlmProvider}
        localLinkedId={localLinkedId}
        setLocalLinkedId={setLocalLinkedId}
        localResultColumns={localResultColumns}
        setLocalResultColumns={setLocalResultColumns}
      />
      {gsOpen && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-[#E5DCE7] overflow-hidden gs-fade-up">
            <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0EAF2] bg-gradient-to-r from-[#5B3A60]/5 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center"><SiGooglesheets className="text-[#5B3A60] text-base" /></div>
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 leading-tight">Google Sheets Setup</h2>
                  <p className="text-[11px] text-gray-500">
                    {isConnecting && "Waiting for Google sign-in..."}
                    {isCheckingSchema && "Checking existing catalog..."}
                    {isLoadingPickerSDK && "Loading file picker..."}
                    {isPickerOpen && "Select your spreadsheet"}
                    {!isConnecting && !isCheckingSchema && !isLoadingPickerSDK && !isPickerOpen && (flow.sheetMeta?.name || "Select a sheet")}
                  </p>
                </div>
              </div>
              <button onClick={() => setGsOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"><FaTimes className="text-sm" /></button>
            </div>
            {!showModalLoading && (
              <div className="px-7 pt-6 pb-3">
                <div className="flex items-center relative">
                  <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-100 -z-10"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] transition-all duration-700" style={{ width: `${((flow.gsStep - 1) / (GS_STEPS.length - 1)) * 100}%` }} /></div>
                  {GS_STEPS.map((s) => {
                    const Icon = s.icon;
                    const done = flow.gsStep > s.num;
                    const curr = flow.gsStep === s.num;
                    return (
                      <div key={s.num} className="flex-1 flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white transition-all duration-300 ${done ? "border-[#5B3A60] bg-[#5B3A60] text-white" : curr ? "border-[#5B3A60] text-[#5B3A60] ring-4 ring-[#5B3A60]/10 scale-110" : "border-gray-200 text-gray-400"}`}>
                          {done ? <FaCheck className="text-xs" /> : <Icon className="text-xs" />}
                        </div>
                        <span className={`text-[10px] mt-1.5 font-bold ${curr ? "text-[#5B3A60]" : done ? "text-gray-500" : "text-gray-400"}`}>{s.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="px-7 pb-7 pt-3 max-h-[60vh] overflow-y-auto gs-scroll">
              {showModalLoading && <div className="gs-fade-up">{modalLoadingContent}</div>}
              {!showModalLoading && flow.gsStep === 1 && (
                <div className="gs-fade-up space-y-4">
                  {flow.existingSchema && (
                    <div className="mb-4 border border-[#DCCFE0] bg-[#FAF7FB] rounded-2xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center flex-shrink-0"><FaDatabase className="text-[#5B3A60]" /></div>
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-[#2F1B2C]">Existing Recommendation Catalog Found</h3>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">Existing catalog found for this account and sheet. Would you like to continue with the existing schema?</p>
                          <div className="flex items-center gap-3 mt-4">
                            <button onClick={() => dispatch({ type: "STEP_CHANGE", step: 2 })} className="px-4 py-2 rounded-xl bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B] transition">Continue Existing</button>
                            <button onClick={() => { dispatch({ type: "SCHEMA_NOT_FOUND" }); loadPickerSDK().then(() => openGooglePicker(flow.googleToken)).catch(() => dispatch({ type: "PICKER_SDK_FAILED" })); }} className="px-4 py-2 rounded-xl border border-[#D4C7D8] text-[#5B3A60] text-xs font-bold hover:bg-[#F8F4F9] transition">Start New</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {hasError && (
                    <ErrorBanner message={flow.error} onRetry={flow.phase === FS.PICKER_SDK_FAILED ? () => { dispatch({ type: "PICKER_SDK_LOADING" }); loadPickerSDK().then(() => openGooglePicker(flow.googleToken)).catch(() => dispatch({ type: "PICKER_SDK_FAILED" })); } : flow.phase === FS.PICKER_CANCELLED && flow.googleToken ? () => { loadPickerSDK().then(() => openGooglePicker(flow.googleToken)); } : !flow.googleToken ? () => { dispatch({ type: "GOOGLE_START" }); startGoogleFlow(); } : null} retryLabel={flow.phase === FS.PICKER_SDK_FAILED ? "Retry Loading" : "Try Again"} />
                  )}
                  {!flow.googleToken ? (
                    <div className="bg-gradient-to-r from-[#5B3A60]/10 to-[#7B5A80]/10 border border-[#5B3A60]/20 rounded-2xl p-5 text-center">
                      <div className="w-12 h-12 rounded-xl bg-[#5B3A60]/10 flex items-center justify-center mx-auto mb-3"><FaGoogle className="text-[#5B3A60] text-lg" /></div>
                      <h3 className="text-sm font-bold text-gray-900 mb-1">Connect Google Account</h3>
                      <p className="text-xs text-gray-500 mb-4">Authorize access to read your Google Sheets</p>
                      <button onClick={() => { dispatch({ type: "GOOGLE_START" }); startGoogleFlow(); }} disabled={isConnecting} className="flex items-center gap-2 px-6 py-2.5 mx-auto rounded-xl bg-white border-2 border-[#5B3A60] text-[#5B3A60] text-sm font-bold hover:bg-[#5B3A60]/5 transition-all disabled:opacity-50">
                        {isConnecting ? <><FaSpinner className="gs-spin text-xs" /> Connecting...</> : <><FaGoogle className="text-red-500" /> Sign in with Google</>}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
                        <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                        <div className="text-xs"><p className="font-bold text-gray-800">Connected as</p><p className="text-gray-600">{flow.googleUser?.email}</p></div>
                        <button onClick={() => dispatch({ type: "DISCONNECT_GOOGLE" })} className="ml-auto text-[10px] text-red-500 font-bold hover:underline">Disconnect</button>
                      </div>
                      {!flow.sheetMeta ? (
                        <div className="space-y-2">
                          <button onClick={() => loadPickerSDK().then(() => openGooglePicker(flow.googleToken)).catch(() => dispatch({ type: "PICKER_SDK_FAILED" }))} className="w-full flex items-center justify-center gap-3 px-5 py-4 border-2 border-dashed border-[#D4C7D8] rounded-xl text-sm font-semibold text-[#5B3A60] hover:border-[#5B3A60] hover:bg-[#5B3A60]/5 transition-all"><SiGooglesheets className="text-lg" /> Select a Google Sheet</button>
                          {flow.phase === FS.PICKER_CANCELLED && (<p className="text-[10px] text-center text-orange-500 font-medium">No sheet was selected. Click above to choose one.</p>)}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs font-bold text-gray-700">Selected Spreadsheet</p>
                          <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl">
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0"><SiGooglesheets className="text-green-600 text-lg" /></div>
                            <div className="flex-1 min-w-0"><p className="text-sm font-bold text-gray-900 truncate">{flow.sheetMeta.name}</p><p className="text-[10px] text-gray-500">{flow.sheetMeta.tabs?.length} worksheets available</p></div>
                            <button onClick={() => dispatch({ type: "SHEET_SELECTED", sheetMeta: null, sheetId: "", defaultTab: "" })} className="text-[10px] text-gray-400 hover:text-red-500 font-bold">Change</button>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-700 mb-2">Select Worksheet</p>
                            <div className="grid grid-cols-2 gap-2">
                              {flow.sheetMeta.tabs?.map((tab) => {
                                const sel = flow.selectedTab === tab;
                                return (
                                  <button key={tab} onClick={() => dispatch({ type: "TAB_CHANGE", tab })} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-left transition-all ${sel ? "border-[#5B3A60] bg-[#5B3A60]/5" : "border-gray-100 hover:border-gray-300"}`}>
                                    <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${sel ? "bg-[#5B3A60] text-white" : "border border-gray-300"}`}>{sel && <FaCheck className="text-[6px]" />}</div>
                                    <span className={`text-xs font-medium truncate ${sel ? "text-[#5B3A60]" : "text-gray-700"}`}>{tab}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-2.5">
                    <SiGooglesheets className="text-blue-500 mt-0.5 flex-shrink-0 text-sm" />
                    <p className="text-xs text-blue-700 leading-relaxed">After selecting a sheet, click "Analyze" to detect columns and assign roles.</p>
                  </div>
                </div>
              )}
              {!showModalLoading && flow.gsStep === 2 && (
                <div className="gs-fade-up space-y-4">
                  {isAnalyzing && (
                    <div className="py-4">
                      <div className="flex flex-col items-center mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-[#5B3A60]/10 flex items-center justify-center mb-3 gs-pulse"><FaDatabase className="text-[#5B3A60] text-xl" /></div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">Analyzing Sheet Structure</h3>
                        <p className="text-xs text-gray-500">Detecting column types and data patterns...</p>
                      </div>
                      <div className="space-y-2.5 mb-5">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="gs-shimmer h-3" style={{ width: 90, animationDelay: `${i * 0.1}s` }} />
                            <div className="gs-shimmer h-3 flex-1" style={{ animationDelay: `${i * 0.15}s` }} />
                            <div className="gs-shimmer h-3" style={{ width: 60, animationDelay: `${i * 0.2}s` }} />
                            <div className="gs-shimmer h-3" style={{ width: 70, animationDelay: `${i * 0.25}s` }} />
                          </div>
                        ))}
                      </div>
                      <div className="bg-gray-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full transition-all duration-300 ease-out" style={{ width: `${Math.min(flow.embeddingProgress, 100)}%` }} /></div>
                      <p className="text-center text-[10px] text-gray-400 mt-2">Scanning worksheet: {flow.selectedTab}</p>
                    </div>
                  )}
                  {flow.phase === FS.ANALYZE_FAILED && (<ErrorBanner message={flow.error} onRetry={handleAnalyze} retryLabel="Retry Analysis" />)}
                  {flow.phase === FS.ANALYZE_TOKEN_EXPIRED && (
                    <div className="space-y-3">
                      <ErrorBanner message={flow.error} />
                      <button onClick={() => { dispatch({ type: "GOOGLE_START" }); setGsOpen(true); startGoogleFlow(); }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[#5B3A60] text-[#5B3A60] text-sm font-bold hover:bg-[#5B3A60]/5 transition-all"><FaGoogle className="text-red-500 text-xs" /> Reconnect Google</button>
                    </div>
                  )}
                  {isAnalyzed && flow.columns.length > 0 && (
                    <div className="gs-fade-up space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{flow.columns.length} Columns Detected</span>
                          <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1"><FaCheckCircle className="text-[8px]" /> Analysis complete</span>
                        </div>
                        <span className="text-[10px] text-[#5B3A60] font-bold">{flow.columns.filter((c) => c.role !== "ignore").length}/{flow.columns.length} active</span>
                      </div>
                      {flow.allColumnsIgnored && (<WarnBanner message="All columns are set to ignore. Assign at least one column a role to proceed." />)}
                      <div className="border border-gray-100 rounded-2xl overflow-hidden">
                        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
                          <div className="col-span-4 text-[9px] font-bold text-gray-500 uppercase tracking-wider">Column</div>
                          <div className="col-span-3 text-[9px] font-bold text-gray-500 uppercase tracking-wider">Type</div>
                          <div className="col-span-5 text-[9px] font-bold text-gray-500 uppercase tracking-wider">Role</div>
                        </div>
                        <div className="divide-y divide-gray-50 max-h-52 overflow-y-auto gs-scroll">
                          {flow.columns.map((col, i) => (
                            <div key={col.name} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-[#5B3A60]/2 transition-colors">
                              <div className="col-span-4">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-bold text-gray-900 font-mono truncate">{col.name}</p>
                                  {col.isNew && (<span className="text-[8px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">NEW</span>)}
                                </div>
                              </div>
                              <div className="col-span-3"><TypeBadge type={col.type} /></div>
                              <div className="col-span-5">
                                <select value={col.role} onChange={(e) => updateColumnRole(i, e.target.value)} className={`w-full border rounded-lg px-2 py-1.5 text-xs font-medium outline-none focus:ring-1 focus:ring-[#5B3A60] transition-all ${col.role === "ignore" ? "border-gray-200 text-gray-400 bg-gray-50" : col.isNew ? "border-purple-300 text-purple-700 bg-purple-50" : "border-[#5B3A60]/30 text-[#5B3A60] bg-[#5B3A60]/5"}`}>
                                  {flow.roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-3 bg-[#5B3A60]/5 border border-[#5B3A60]/20 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div><p className="text-xs font-bold text-[#4B334F]">Scoring Weights</p><p className="text-[10px] text-gray-500">{getWeightSummary()}</p></div>
                          <button onClick={() => setWeightsModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B] transition-all"><FaSlidersH className="text-[10px]" /> Configure</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!showModalLoading && flow.gsStep === 3 && (
                <div className="gs-fade-up space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    {[{ label: "Sheet", val: flow.sheetMeta?.name || "" }, { label: "Tab", val: flow.selectedTab }, { label: "Columns", val: `${flow.columns.filter((c) => c.role !== "ignore").length} active` }].map((item) => (
                      <div key={item.label} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="text-xs font-bold text-gray-900 truncate">{item.val}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Embed Template</label>
                      <span className="text-[10px] text-gray-400">{flow.columns.filter((c) => c.role !== "ignore").length} fields</span>
                    </div>
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2">
                        {flow.columns.filter((c) => c.role !== "ignore").map((col) => (
                          <button key={col.name} type="button" onClick={() => handleTemplateChange(`${flow.embedTemplate} {${col.name}}`)} className="px-3 py-1 rounded-full bg-[#F3EDF5] border border-[#D8C9DD] text-[#5B3A60] text-xs font-semibold">{`{${col.name}}`}</button>
                        ))}
                      </div>
                    </div>
                    <textarea rows={3} value={flow.embedTemplate} onChange={(e) => handleTemplateChange(e.target.value)} disabled={isPreviewing} className="w-full border border-[#D4C7D8] rounded-xl px-4 py-3 text-sm font-mono text-gray-700 focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none bg-white disabled:opacity-60" placeholder="e.g. {title} {description} {price}" />
                    <p className="text-[10px] text-gray-400 mt-1">Use &#123;column_name&#125; placeholders to define the embedding text.</p>
                    {flow.invalidTemplatePlaceholders?.length > 0 && (<WarnBanner message={`Unknown placeholders: ${flow.invalidTemplatePlaceholders.join(", ")}. These columns don't exist or are ignored.`} />)}
                  </div>
                  {flow.phase === FS.PREVIEW_FAILED && (<ErrorBanner message={flow.error} onRetry={handlePreview} retryLabel="Retry Preview" />)}
                  {flow.previewEmpty && flow.phase === FS.PREVIEWED && (<WarnBanner message="No preview content was generated. Check your template and column roles." />)}
                  <div className="flex items-center gap-3">
                    <button onClick={handlePreview} disabled={isPreviewing || !!flow.invalidTemplatePlaceholders?.length || flow.allColumnsIgnored} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[#5B3A60] text-[#5B3A60] text-sm font-bold hover:bg-[#5B3A60]/5 transition-all disabled:opacity-50">
                      {isPreviewing ? <><FaSpinner className="gs-spin text-xs" /> Previewing...</> : <><FaEye className="text-xs" /> Preview</>}
                    </button>
                  </div>
                  {!isPreviewed && <p className="text-[10px] text-gray-400">Run Preview first to enable embedding.</p>}
                  {flow.previewData.length > 0 && (
                    <div className="gs-fade-up border border-[#E5DCE7] rounded-2xl overflow-hidden">
                      <div className="px-4 py-2.5 bg-[#5B3A60]/5 border-b border-[#E5DCE7] flex items-center gap-2"><FaLayerGroup className="text-[#5B3A60] text-xs" /><span className="text-xs font-bold text-[#5B3A60]">Preview — {flow.previewData.length} samples</span></div>
                      <div className="divide-y divide-gray-50 max-h-48 overflow-y-auto gs-scroll">
                        {flow.previewData.map((item, i) => (
                          <div key={i} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start gap-2.5">
                              <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0">Row {item.row_index}</span>
                              <p className="text-xs text-[#5B3A60] font-medium leading-relaxed">{item.embed_text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!showModalLoading && flow.gsStep === 4 && (
                <div className="gs-fade-up space-y-5">
                  {flow.reembedMode ? (
                    <>
                      <div className="flex flex-col items-center text-center py-3">
                        <div className="w-16 h-16 rounded-full bg-[#5B3A60]/10 flex items-center justify-center mb-4"><FaRedo className="text-[#5B3A60] text-3xl" /></div>
                        <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Review & Edit</h3>
                        <p className="text-sm text-gray-500 max-w-md leading-relaxed">Review your current configuration. You can edit the template or weights below before updating.</p>
                      </div>
                      <ChangeSummary changes={computeChanges(flow.originalColumns, flow.columns, flow.originalTemplate, flow.embedTemplate, flow.originalWeights, flow.scoringWeights)} hasNewRows={flow.analyzedRowCount > flow.storedRowCount} storedRowCount={flow.storedRowCount} analyzedRowCount={flow.analyzedRowCount} />
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold text-[#4B334F]">Embed Template</h3>
                            <span className="text-[10px] text-gray-500">Edit to change embedding structure</span>
                          </div>
                          <div className="mb-2">
                            <div className="flex flex-wrap gap-2">
                              {flow.columns.filter((c) => c.role !== "ignore").map((col) => (
                                <button key={col.name} type="button" onClick={() => { const newTemplate = `${flow.embedTemplate} {${col.name}}`; dispatch({ type: "EMBED_TEMPLATE_CHANGE", template: newTemplate, invalidPlaceholders: [] }); }} className="px-2 py-1 rounded-md bg-[#F3EDF5] border border-[#D8C9DD] text-[#5B3A60] text-[10px] font-semibold hover:bg-[#E8DDE8]">{`{${col.name}}`}</button>
                              ))}
                            </div>
                          </div>
                          <textarea rows={3} value={flow.embedTemplate} onChange={(e) => { const invalid = validateTemplatePlaceholders(e.target.value, flow.columns); dispatch({ type: "EMBED_TEMPLATE_CHANGE", template: e.target.value, invalidPlaceholders: invalid }); }} className="w-full border border-[#D4C7D8] rounded-xl px-4 py-3 text-sm font-mono text-gray-700 focus:ring-2 focus:ring-[#5B3A60] outline-none resize-none bg-white" placeholder="e.g. {title} {description}" />
                          {flow.invalidTemplatePlaceholders?.length > 0 && (<WarnBanner message={`Unknown placeholders: ${flow.invalidTemplatePlaceholders.join(", ")}`} />)}
                        </div>
                        <div className="p-3 bg-[#5B3A60]/5 border border-[#5B3A60]/20 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div><p className="text-xs font-bold text-[#4B334F]">Scoring Weights</p><p className="text-[10px] text-gray-500">{getWeightSummary()}</p></div>
                            <button onClick={() => setWeightsModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5B3A60] text-white text-xs font-bold hover:bg-[#462B4B] transition-all"><FaSlidersH className="text-[10px]" /> Configure</button>
                          </div>
                        </div>
                      </div>
                      {flow.phase === FS.EMBED_FAILED && (<ErrorBanner message={flow.error} />)}
                      <div className="pt-3 flex justify-center">
                        <button onClick={handleReembed} disabled={isEmbedding} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-sm font-bold hover:shadow-lg hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                          {isEmbedding ? <><FaSpinner className="gs-spin text-sm" /> Updating...</> : <><FaRedo className="text-xs" /> Confirm & Update</>}
                        </button>
                      </div>
                      {isEmbedding && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-gray-600">Progress</span><span className="text-xs font-bold text-[#5B3A60]">{Math.round(flow.embeddingProgress)}%</span></div>
                          <div className="bg-gray-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full transition-all duration-300 ease-out" style={{ width: `${flow.embeddingProgress}%` }} /></div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col items-center text-center py-3">
                        <div className="w-16 h-16 rounded-full bg-[#5B3A60]/10 flex items-center justify-center mb-4"><FaCheckCircle className="text-[#5B3A60] text-3xl" /></div>
                        <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Ready to Embed</h3>
                        <p className="text-sm text-gray-500 max-w-md leading-relaxed">Review your settings and confirm to embed this Google Sheet into your agent's knowledge base.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border border-[#ECE4EE] rounded-2xl bg-[#FAF8FB] p-5"><p className="text-2xl font-extrabold text-gray-900 truncate">{flow.sheetMeta?.name}</p><p className="text-xs uppercase tracking-wider text-gray-500 mt-2 font-bold">Spreadsheet</p></div>
                        <div className="border border-[#ECE4EE] rounded-2xl bg-[#FAF8FB] p-5"><p className="text-2xl font-extrabold text-gray-900 truncate">{flow.selectedTab}</p><p className="text-xs uppercase tracking-wider text-gray-500 mt-2 font-bold">Worksheet</p></div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between"><h3 className="text-sm font-bold text-[#4B334F]">Selected Columns</h3><span className="text-[11px] text-gray-500 font-medium">{flow.columns.filter((c) => c.role !== "ignore").length} active columns</span></div>
                        <div className="border border-[#E8DDE8] rounded-2xl overflow-hidden">
                          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#FAF7FB] border-b border-[#EFE5F1]">
                            <div className="col-span-5 text-[10px] font-bold uppercase text-gray-500">Column</div>
                            <div className="col-span-3 text-[10px] font-bold uppercase text-gray-500">Type</div>
                            <div className="col-span-4 text-[10px] font-bold uppercase text-gray-500">Role</div>
                          </div>
                          <div className="max-h-56 overflow-y-auto gs-scroll divide-y divide-[#F4EDF5]">
                            {flow.columns.filter((c) => c.role !== "ignore").map((col) => (
                              <div key={col.name} className="grid grid-cols-12 gap-2 px-4 py-3 items-center bg-white">
                                <div className="col-span-5"><p className="text-xs font-bold text-gray-900 truncate">{col.name}</p></div>
                                <div className="col-span-3"><TypeBadge type={col.type} /></div>
                                <div className="col-span-4"><span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#5B3A60]/10 text-[#5B3A60] border border-[#5B3A60]/20">{col.role}</span></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between"><h3 className="text-sm font-bold text-[#4B334F]">Embed Template</h3><span className="text-[10px] text-gray-500">Final embedding structure</span></div>
                        <div className="border border-[#E8DDE8] rounded-2xl bg-[#FCFAFC] p-4"><p className="text-xs leading-6 text-[#4B334F] break-words font-mono">{flow.embedTemplate}</p></div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between"><h3 className="text-sm font-bold text-[#4B334F]">Scoring Weights</h3><span className="text-[10px] text-gray-500">Final ranking priorities</span></div>
                        <div className="border border-[#E8DDE8] rounded-2xl overflow-hidden">
                          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#FAF7FB] border-b border-[#EFE5F1]">
                            <div className="col-span-7 text-[10px] font-bold uppercase text-gray-500">Dimension</div>
                            <div className="col-span-5 text-[10px] font-bold uppercase text-gray-500">Weight</div>
                          </div>
                          <div className="divide-y divide-[#F4EDF5]">
                            {Object.entries(flow.scoringWeights).map(([key, value]) => (
                              <div key={key} className="grid grid-cols-12 gap-2 px-4 py-3 items-center bg-white">
                                <div className="col-span-7"><p className="text-xs font-semibold text-gray-800 break-all">{key}</p></div>
                                <div className="col-span-5">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 h-2 rounded-full bg-[#EFE5F1] overflow-hidden"><div className="h-full bg-[#5B3A60] rounded-full" style={{ width: `${Math.min(value * 100, 100)}%` }} /></div>
                                    <span className="text-[11px] font-bold text-[#5B3A60] min-w-[42px] text-right">{value.toFixed(3)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="pt-3 flex justify-center">
                        <button onClick={handleFinalize} disabled={isEmbedding} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-sm font-bold hover:shadow-lg hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                          {isEmbedding ? <><FaSpinner className="gs-spin text-sm" /> Embedding...</> : <>Confirm & Embed</>}
                        </button>
                      </div>
                      {isEmbedding && flow.embedStage >= 0 && (
                        <div className="slide-in-right mt-2 px-4 py-3 bg-[#5B3A60]/5 border border-[#5B3A60]/20 rounded-xl flex items-center gap-3"><FaSpinner className="gs-spin text-[#5B3A60] text-sm flex-shrink-0" /><p className="text-xs font-medium text-[#5B3A60]">{EMBED_STAGES[flow.embedStage]}</p></div>
                      )}
                      {isEmbedding && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-gray-600">Progress</span><span className="text-xs font-bold text-[#5B3A60]">{Math.round(flow.embeddingProgress)}%</span></div>
                          <div className="bg-gray-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] rounded-full transition-all duration-300 ease-out" style={{ width: `${flow.embeddingProgress}%` }} /></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {!showModalLoading && !isEmbedded && !isEmbedding && (
              <div className="flex items-center justify-between px-7 py-4 border-t border-[#F0EAF2] bg-gray-50/50">
                <button onClick={() => { if (flow.gsStep > 1) { dispatch({ type: "STEP_BACK" }); } else { if (flow.reembedMode) dispatch({ type: "EXIT_REEMBED_MODE" }); setGsOpen(false); } }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 transition-all"><FaChevronLeft className="text-xs" /> {flow.gsStep === 1 ? "Cancel" : "Back"}</button>
                {flow.gsStep === 1 && (
                  <button onClick={handleAnalyze} disabled={!flow.googleToken || !flow.sheetMeta || !flow.selectedTab || isAnalyzing} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100">
                    {isAnalyzing ? <><FaSpinner className="gs-spin text-xs" /> Analyzing...</> : <><FaBolt className="text-xs" /> Analyze Sheet <FaChevronRight className="text-xs" /></>}
                  </button>
                )}
                {flow.gsStep === 2 && isAnalyzed && !flow.allColumnsIgnored && (
                  <button onClick={() => dispatch({ type: "STEP_CHANGE", step: flow.reembedMode ? 4 : 3 })} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all">{flow.reembedMode ? "Review Changes" : "Preview"} <FaChevronRight className="text-xs" /></button>
                )}
                {flow.gsStep === 3 && isPreviewed && !flow.previewEmpty && (
                  <button onClick={() => dispatch({ type: "STEP_CHANGE", step: 4 })} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#5B3A60] to-[#7B5A80] text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all">Finalize <FaChevronRight className="text-xs" /></button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <WeightsModal open={weightsModalOpen} onClose={() => setWeightsModalOpen(false)} columns={flow.columns} weights={flow.scoringWeights} onSave={(weights) => { dispatch({ type: "WEIGHTS_CHANGE", scoringWeights: weights }); showToast("success", "Weights saved"); }} />
      <ConfigViewModal open={configModalOpen} onClose={() => setConfigModalOpen(false)} flow={flow} />
      <ReembedReviewModal
        open={showReembedReview}
        onClose={() => setShowReembedReview(false)}
        data={schemaUpdateData}
        flow={flow}
        onUpdateTemplate={handleTemplateChange}
        onOpenWeights={() => setWeightsModalOpen(true)}
        onConfirm={handleConfirmReembed}
        isConfirming={schemaUpdateLoading || reembedOp.loading}
        dispatch={dispatch}
      />
      <DeleteConfirmModal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDeleteCatalog} flow={flow} />
    </div>
  );
}

function computeChanges(originalCols, currentCols, originalTemplate, currentTemplate, originalWeights, currentWeights) {
  const origMap = new Map(originalCols.map(c => [c.name, c]));
  const currMap = new Map(currentCols.filter(c => c.role !== "ignore").map(c => [c.name, c]));
  const newColumns = currentCols.filter(c => !origMap.has(c.name) && c.role !== "ignore");
  const roleChanged = currentCols.filter(c => { const orig = origMap.get(c.name); return orig && orig.role !== c.role; });
  const removedColumns = originalCols.filter(c => !currMap.has(c.name) && c.role !== "ignore");
  const templateChanged = originalTemplate.trim() !== currentTemplate.trim();
  const weightsChanged = JSON.stringify(originalWeights) !== JSON.stringify(currentWeights);
  const hasColumnChanges = newColumns.length > 0 || roleChanged.length > 0 || removedColumns.length > 0 || templateChanged || weightsChanged;
  return { newColumns, roleChanged, removedColumns, templateChanged, weightsChanged, hasColumnChanges };
}